import React, { useState, useEffect, useRef } from 'react';
import Snackbar from './Snackbar';
import FlashMessage from './FlashMessage';
import { useAutoScroll } from '../hooks/useAutoScroll';
import { API_ENDPOINTS, getApiUrl } from '../config/api';
import { maskEngineChassisNumber, maskChassisNumber } from '../utils/maskUtils';
import { trackPaymentInitiated } from '../services/analyticsService';
import { logEvent } from '../utils/appLogger';
import { mergeAgentContextIntoRequestBody, getPersistedAgentTransactionContext, persistAgentTransactionContext } from '../utils/agentTransactionContext';
import { EXTENSION_SEED_USER_MESSAGE } from '../utils/extensionSeed';
import { grossFromNet } from '../utils/productRatesApi';
import { canonicalCoverPeriodForProduct, isTpoCoverPeriodChosen } from '../utils/coverPeriodTerms';
import { inclusiveEndForCoverPeriod } from '../utils/calendarDates';
import {
  formatPolicyDate,
  isDoubleInsuranceFailure,
  latestPolicyEndFromList
} from '../utils/doubleInsuranceRetry';
import { validateKenyanInsuredPhone, withInsuredPhoneFromMobile } from '../utils/kenyanPhoneValidation';
import { validateKenyanVehicleRegistration } from '../utils/kenyanRegistrationValidation';
import { getInstitutionalPllFee } from '../utils/institutionalPll';

const createInitialDmvicCertificateState = () => ({
  isIssuing: false,
  certificateNumber: null,
  transactionNumber: null,
  certificateUrl: null, // DMVIC PDF URL when available (from poll or issue response) – used for one-click download
  error: null,
  errorCode: null,   // DMVIC/API error code (e.g. ER005) – shown for rectification
  errorText: null,   // Exact error text from issuer – shown for rectification
  retryCount: 0,
  maxRetries: 10, // Increased from 5 to 10 since we now have better flags (IsLogBookVerified: true, IsVehicleInspected: true)
  queuedForRetry: false,
  nextRetryAt: null,
  issuanceRequestID: null, // Store IssuanceRequestID for ER007 confirmation
  pendingConfirmation: false, // Track if waiting for user confirmation
  hardError: false, // NEW: Track if hard error (ER005, ER006) that blocks payment
  failedJobId: null, // When BullMQ state is 'failed' — use POST /certificate-status/:id/retry
  certificateJobId: null, // Ongoing or stalled poll: job may still be active; use preflight/re-poll, not job.retry
  issuingElapsedMs: 0,
  issuingPhase: null,
  issuingQueueState: null,
  recordingOnCore: false,
  stillWaiting: false
});

const isCertificateDownloadReady = (data) => {
  if (!data || typeof data !== 'object') return false;
  if (data.downloadReady) return true;
  return !!String(data.certificateNumber || '').trim();
};

const isPendingPolicySaveToast = (message) => /saving your policy/i.test(String(message || ''));

const paidCertJobIdFromRef = (referenceNumber) => {
  const ref = String(referenceNumber || '').trim();
  if (!ref) return null;
  return `paid-cert-${ref.replace(/[^A-Za-z0-9._-]/g, '_').slice(0, 90)}`;
};

const issuingWaitCopy = (elapsedMs, phase, queueState) => {
  const elapsed = Number(elapsedMs) || 0;
  const p = String(phase || '').toLowerCase();
  const s = String(queueState || '').toLowerCase();
  if (elapsed >= 60000) {
    return {
      title: 'Taking a bit longer than usual',
      body: 'Stay on this page. Download will appear as soon as it is ready. You can also leave and get it from your dashboard.'
    };
  }
  if (s === 'waiting' || s === 'delayed' || s === 'paused' || p === 'queued') {
    return {
      title: 'In the queue',
      body: 'Payment received. You are next — issuing starts in a moment.'
    };
  }
  if (p === 'checking_cover') {
    return {
      title: 'Checking existing cover',
      body: 'Making sure this vehicle is clear to issue. This takes a few seconds.'
    };
  }
  if (p === 'issuing' || p === 'preparing') {
    if (elapsed < 20000) {
      return {
        title: 'Sending to DMVIC',
        body: 'Payment received. Registering your cover with DMVIC…'
      };
    }
    return {
      title: 'Still working with DMVIC',
      body: 'Still working with DMVIC. This usually takes under a minute.'
    };
  }
  if (elapsed < 20000) {
    return {
      title: 'Registering your cover',
      body: 'Payment received. Registering your cover…'
    };
  }
  return {
    title: 'Still working with DMVIC',
    body: 'Still working with DMVIC. This usually takes under a minute.'
  };
};

const buildCheckoutTransactionKey = ({
  coverType,
  selectedClass,
  startDate,
  endDate,
  coverPeriod,
  insuredIdNumber,
  vehicleRegistrationNumber
}) => (
  [
    String(coverType || '').trim().toUpperCase(),
    String(selectedClass || '').trim().toUpperCase(),
    String(startDate || '').trim().slice(0, 10),
    String(endDate || '').trim().slice(0, 10),
    String(coverPeriod || '').trim().toUpperCase(),
    String(insuredIdNumber || '').trim().toUpperCase(),
    String(vehicleRegistrationNumber || '').trim().toUpperCase()
  ].join('|')
);

const Checkout = ({ coverType, selectedClass, institutionType = null, startDate, endDate, coverPeriod, insuredData, vehicleData, premium, agentId, agency, agentUserId, agentTransactionContext: agentContextProp, isUnderwriting, onBack, onPaymentComplete, extensionContext = null, extensionSeedBlocked = false, renewalContext = null }) => {
  // Use payload set when agent started purchase so business is linked to them (from CoverPurchase or sessionStorage after refresh)
  const agentTransactionContext = agentContextProp || getPersistedAgentTransactionContext();
  const resolvedInstitutionType =
    selectedClass === 'Motor Commercial Institutional'
      ? (institutionType || vehicleData?.institutionType || 'school')
      : null;
  const vehicleDataForSave = resolvedInstitutionType
    ? { ...(vehicleData || {}), institutionType: resolvedInstitutionType }
    : vehicleData;
  const [agentTransactionContextOverride, setAgentTransactionContextOverride] = useState(null);
  const resolvedAgentTransactionContext = agentTransactionContextOverride || agentTransactionContext;
  const [lastPaymentError, setLastPaymentError] = useState(null); // { code?: string|null, message?: string }
  const [mobileNumber, setMobileNumber] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('pesapal'); // 'pesapal' | 'ecobank'
  const [ecobankMpesaCode, setEcobankMpesaCode] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [showSnackbar, setShowSnackbar] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarType, setSnackbarType] = useState('info');
  const [snackbarDuration, setSnackbarDuration] = useState(5000); // Default 5 seconds, 0 = persistent
  const [showFlash, setShowFlash] = useState(false);
  const [flashMessage, setFlashMessage] = useState('');
  const [flashType, setFlashType] = useState('info');
  const [paymentStep, setPaymentStep] = useState('input'); // input, processing, success, failed
  const [clientId, setClientId] = useState(null); // Store clientId from saved application
  const [policyNumber, setPolicyNumber] = useState(null); // From submit-application for receipt
  const [reserveState, setReserveState] = useState({ status: 'idle', error: null, retryCount: 0 }); // idle|reserving|retrying|ready|failed
  const [reserveRetryNonce, setReserveRetryNonce] = useState(0);
  const [isValidatingCertificate, setIsValidatingCertificate] = useState(false); // Track certificate validation state
  const [certificateValidated, setCertificateValidated] = useState(false); // Track if certificate has been validated
  const certificateJobPollRef = useRef(null); // clear job-status polling on unmount
  const issuingClockRef = useRef(null);
  const issuingVisibilityCleanupRef = useRef(null);
  const certificateIssuedRef = useRef(false);
  const saveRetryTimeoutRef = useRef(null);
  const reserveCoverCalledRef = useRef(false);
  const reserveRetryTimeoutRef = useRef(null);
  const lastReservedTermsRef = useRef(null);
  const [dmvicCertificate, setDmvicCertificate] = useState(createInitialDmvicCertificateState);
  const issuingStatusCopy = issuingWaitCopy(
    dmvicCertificate.issuingElapsedMs,
    dmvicCertificate.issuingPhase,
    dmvicCertificate.issuingQueueState
  );
  const [hasProceededToReceipt, setHasProceededToReceipt] = useState(false);
  const checkoutTransactionKey = buildCheckoutTransactionKey({
    coverType,
    selectedClass,
    startDate,
    endDate,
    coverPeriod,
    insuredIdNumber: insuredData?.idNumber,
    vehicleRegistrationNumber: vehicleData?.registrationNumber
  });
  const [rectificationState, setRectificationState] = useState({
    emailAddress: insuredData?.emailAddress || insuredData?.email || '',
    startDate: startDate || '',
    suggestedStartDate: null,
    existingPolicyEndDate: null,
    existingPolicies: [],
    suggestionError: null,
    loadingSuggestion: false,
    applyError: null
  });
  const doubleInsuranceFetchKeyRef = useRef(null);
  const isValidEmail = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || '').trim());
  const isInvalidEmailFailure = (errorCode, errorText, errorMessage) => {
    const combined = `${errorCode || ''} ${errorText || ''} ${errorMessage || ''}`.toLowerCase();
    return errorCode === 'INVALID_EMAIL' ||
      (combined.includes('email') && (combined.includes('invalid') || combined.includes('required') || combined.includes('must be valid')));
  };
  const submissionCoverType = coverType === 'TPO_EXTEND' ? 'TPO' : coverType;
  const isCompCover = String(coverType || '').toUpperCase() === 'COMP';
  const isTpoExtendSecondHalf = coverType === 'TPO_EXTEND' && !!extensionContext?.policyNo;
  /** Sent to Core/DB as the pricing bucket; UI uses `submissionCoverPeriodDisplay` for clearer copy. */
  const submissionCoverPeriod =
    coverType === 'TPO_EXTEND'
      ? (isTpoExtendSecondHalf ? '1 Year (50%)' : coverPeriod)
      : canonicalCoverPeriodForProduct(
          coverType,
          coverPeriod,
          selectedClass,
          extensionContext ? { extensionMode: true, productVariant: 'TPO_EXTEND' } : {}
        );
  const submissionCoverPeriodDisplay =
    coverType === 'TPO_EXTEND' ? (isTpoExtendSecondHalf ? 'Remaining period (50%)' : coverPeriod) : submissionCoverPeriod;
  const submissionEndDate =
    coverType === 'TPO_EXTEND'
      ? isTpoExtendSecondHalf
        ? extensionContext?.annualEndDate || endDate || ''
        : endDate
      : (startDate && submissionCoverPeriod
          ? inclusiveEndForCoverPeriod(String(startDate).slice(0, 10), submissionCoverPeriod) || endDate
          : endDate);
  const extensionPolicyBalance = Number(extensionContext?.sourcePolicy?.policyBalance ?? 0);
  const hasExtensionOutstandingBalance =
    coverType === 'TPO_EXTEND' && Number.isFinite(extensionPolicyBalance) && extensionPolicyBalance > 0;

  /** Block payment if the portal-derived annual end for the extension was never set. */
  const getTpoExtendPaymentBlockMessage = () => {
    if (coverType !== 'TPO_EXTEND' || !extensionContext?.policyNo) return null;
    if (!extensionContext?.annualEndDate) {
      return 'Extension end date missing.';
    }
    return null;
  };
  // Extension installment amount is the remaining 50% premium (already computed by TPO_EXTEND pricing).
  const normalizePricingClass = (value) => String(value || '').toLowerCase().replace(/[^a-z0-9]/g, '');
  const selectedClassKey = normalizePricingClass(selectedClass);
  const selectedCoverTypeKey = String(coverType || '').toUpperCase();
  const FIXED_CLASS_OVERRIDES = {
    TPO: {
      motorprivate: { net: 3300, gross: 3621 },
      motorcycleprivate: { net: 1870, gross: 2049 },
      tuktukcommercial: { net: 2784, gross: 3054 },
      tractortpo: { net: 2784, gross: 3054 },
      primemovers: { net: 9185, gross: 10085 },
      motorcyclepsvannual: { net: 3241, gross: 3556 },
      tuktukpsv: { net: 4155, gross: 4560 },
      motorcyclepsv6months: { net: 1870, gross: 2049 }
    },
    TOR: {
      motorprivate: { net: 600, gross: 600 },
      motorcommercial: { net: 600, gross: 600 }
    }
  };
  const MOTOR_COMMERCIAL_TPO_BANDS = [
    { max: 3, net: 4155, gross: 4560 },
    { max: 8, net: 5070, gross: 5565 },
    { max: 12, net: 5985, gross: 6569 },
    { max: 15, net: 6899, gross: 7574 },
    { max: 20, net: 9185, gross: 10085 },
    { max: Number.POSITIVE_INFINITY, net: 13758, gross: 15108 }
  ];
  const tonnageBasedOverrideClassKeys = new Set([
    'motorcommercialgeneralcartage',
    'motorcommercialowngoods'
  ]);
  const applyTpoExtendHalf = (pair) => {
    if (!pair || selectedCoverTypeKey !== 'TPO_EXTEND') return pair;
    return {
      net: Math.round(Number(pair.net || 0) * 0.5),
      gross: Math.round(Number(pair.gross || 0) * 0.5)
    };
  };
  const resolveClassPricingOverride = () => {
    const pricingCoverKey =
      selectedCoverTypeKey === 'TPO_EXTEND' ? 'TPO' : selectedCoverTypeKey;
    if (pricingCoverKey !== 'TPO' && pricingCoverKey !== 'TOR') return null;
    if (pricingCoverKey === 'TPO' && selectedClassKey === 'motorcyclepsv') {
      const periodKey = String(coverPeriod || '').toLowerCase();
      const pair = periodKey.includes('6') ? { net: 1870, gross: 2049 } : { net: 3241, gross: 3556 };
      return applyTpoExtendHalf(pair);
    }
    if (pricingCoverKey === 'TPO' && selectedClassKey === 'motorcommercialinstitutional') {
      const pll = Number(vehicleData?.licensedToCarry);
      if (Number.isFinite(pll) && pll > 0) {
        // Underwriting-approved Institutional TPO model:
        // basic = (pll <= 14 ? 5000 : 7500) + (pll * fee)
        // fee = 250 schools | 500 other institutions
        // gross = round((basic * 1.0045) + 40)
        // net-to-pay = round(gross - (basic * 10%) + ((basic * 10%) * 10%))
        const pllFee = getInstitutionalPllFee(institutionType || vehicleData?.institutionType);
        const basic = (pll > 14 ? 7500 : 5000) + (pll * pllFee);
        const gross = Math.round((basic * 1.0045) + 40);
        const commission = basic * 0.10;
        const withholdingTax = commission * 0.10;
        const net = Math.round(gross - commission + withholdingTax);
        return applyTpoExtendHalf({ net, gross });
      }
    }
    if (pricingCoverKey === 'TPO' && selectedClassKey === 'psvchaufferdriven') {
      const pll = Number(vehicleData?.licensedToCarry);
      if (Number.isFinite(pll) && pll > 0) {
        // Underwriting-approved PSV Chauffer Driven TPO model:
        // gross basic = 5500 + (PLL × 500)
        // gross payable = gross basic × 1.0045 + 40 (rounded to nearest shilling)
        // net payable (for toggle) follows approved rounded decomposition: 5068 + (PLL × 457)
        const gross = Math.round(((5500 + (pll * 500)) * 1.0045) + 40);
        const net = Math.round(5068 + (pll * 457));
        return applyTpoExtendHalf({ net, gross });
      }
    }
    const fixed = FIXED_CLASS_OVERRIDES[pricingCoverKey]?.[selectedClassKey] || null;
    if (fixed) return applyTpoExtendHalf(fixed);
    if (pricingCoverKey === 'TPO' && tonnageBasedOverrideClassKeys.has(selectedClassKey)) {
      const tonnage = Number(vehicleData?.tonnage);
      if (!Number.isFinite(tonnage) || tonnage <= 0) return null;
      const band = MOTOR_COMMERCIAL_TPO_BANDS.find((b) => tonnage <= b.max) || null;
      return applyTpoExtendHalf(band);
    }
    return null;
  };
  const selectedClassPricingOverride = resolveClassPricingOverride();
  const isPricingModeEnabledClass = !!selectedClassPricingOverride;

  const tpoNetFromGross = (grossPremium) => {
    const gross = Number(grossPremium);
    if (!Number.isFinite(gross)) return 0;
    const stampDuty = 40;
    const grossRounded = Math.round(gross);
    const grossMinusStamp = grossRounded - stampDuty;
    if (grossMinusStamp <= 0) return 0;
    const approx = grossMinusStamp / 1.0045;
    const start = Math.max(0, Math.floor(approx) - 10);
    const end = Math.max(start, Math.ceil(approx) + 10);
    let bestNet = Math.round(approx);
    let bestDelta = Number.POSITIVE_INFINITY;
    for (let net = start; net <= end; net++) {
      const phcf = Math.round(net * 0.0025);
      const trainingLevy = parseFloat((net * 0.002).toFixed(2));
      const reconstructedGross = net + phcf + trainingLevy + stampDuty;
      const delta = Math.abs(reconstructedGross - grossRounded);
      if (delta === 0) return net;
      if (delta < bestDelta || (delta === bestDelta && net < bestNet)) {
        bestDelta = delta;
        bestNet = net;
      }
    }
    return bestNet;
  };

  const [pricingMode, setPricingMode] = useState('NET'); // NET | GROSS (override classes only)
  useEffect(() => {
    if (!isPricingModeEnabledClass && pricingMode !== 'NET') setPricingMode('NET');
  }, [isPricingModeEnabledClass, pricingMode]);

  const netAmountFromRate = Number.isFinite(Number(premium?.baseAmount))
    ? Math.round(Number(premium.baseAmount))
    : null;
  const grossFromApi = Number.isFinite(Number(premium?.grossAmount)) && Number(premium.grossAmount) > 0
    ? Math.round(Number(premium.grossAmount))
    : null;
  const grossFromAmount = Number.isFinite(Number(premium?.amount)) && Number(premium.amount) > 0
    ? Math.round(Number(premium.amount))
    : null;
  const grossCandidate = grossFromApi != null ? grossFromApi : grossFromAmount;
  const hasDistinctDbGross = grossCandidate != null
    && (netAmountFromRate == null || grossCandidate !== netAmountFromRate);
  const grossAmountFromRate = hasDistinctDbGross ? grossCandidate : null;
  const overrideNetAmount = selectedClassPricingOverride?.net ?? null;
  const overrideGrossAmount = selectedClassPricingOverride?.gross ?? null;
  // For mapped classes, trust the configured override pair first. Some checkout flows provide only one rate value.
  const selectedNetAmount = overrideNetAmount != null
    ? overrideNetAmount
    : (netAmountFromRate != null ? netAmountFromRate : tpoNetFromGross(grossAmountFromRate || 0));
  const selectedGrossAmount = overrideGrossAmount != null
    ? overrideGrossAmount
    : (grossAmountFromRate != null ? grossAmountFromRate : grossFromNet(selectedNetAmount));
  const pricingSource = isPricingModeEnabledClass
    ? (overrideNetAmount != null && overrideGrossAmount != null ? 'CLASS_OVERRIDE' : (grossAmountFromRate != null ? 'DB' : (netAmountFromRate != null ? 'DB + fallback gross' : 'FALLBACK')))
    : 'STANDARD';
  const payableAmount = isPricingModeEnabledClass
    ? (pricingMode === 'GROSS' ? selectedGrossAmount : selectedNetAmount)
    : Number(premium?.amount || 0);
  const isDirectBusiness = !!(resolvedAgentTransactionContext?.isDirectBusiness);
  const commissionAllowed = isDirectBusiness ? 'No' : (isPricingModeEnabledClass ? 'Yes' : null);
  const modePillStyle = pricingMode === 'GROSS'
    ? {
      display: 'inline-flex',
      alignItems: 'center',
      padding: '2px 8px',
      borderRadius: 999,
      fontSize: '0.78rem',
      fontWeight: 700,
      letterSpacing: '0.02em',
      color: '#7a4b00',
      background: '#fff3cd',
      border: '1px solid #ffe69c',
      margin: '0 2px'
    }
    : {
      display: 'inline-flex',
      alignItems: 'center',
      padding: '2px 8px',
      borderRadius: 999,
      fontSize: '0.78rem',
      fontWeight: 700,
      letterSpacing: '0.02em',
      color: '#0f5132',
      background: '#d1e7dd',
      border: '1px solid #badbcc',
      margin: '0 2px'
    };

  // Smart phone number formatting function
  const smartFormatPhoneNumber = (phoneNumber) => {
    if (!phoneNumber) return '';
    
    // Remove all non-digit characters
    let cleaned = phoneNumber.replace(/[^0-9]/g, '');
    
    // Auto-format based on detected pattern
    if (cleaned.startsWith('254')) {
      // Already has country code
      return '+' + cleaned;
    } else if (cleaned.startsWith('0') && cleaned.length === 10) {
      // Kenyan format: 072X XXXXXX -> +254 72X XXXXXX
      return '+254' + cleaned.substring(1);
    } else if (cleaned.length === 9) {
      // Just 9 digits -> add country code
      return '+254' + cleaned;
    } else if (cleaned.length === 12 && cleaned.startsWith('254')) {
      // Already 12 digits with country code
      return '+' + cleaned;
    } else if (cleaned.length >= 9 && cleaned.length <= 12) {
      // Preserve what user typed
      return phoneNumber.startsWith('+') ? phoneNumber : '+254' + cleaned.substring(3);
    }
    
    return phoneNumber;
  };
  
  // Pre-fill M-Pesa number from insured details only when it is a valid Kenyan mobile
  useEffect(() => {
    if (!insuredData?.phoneNumber || mobileNumber) return;
    const vehicleCtx = {
      vehicleValue: vehicleData?.vehicleValue,
      sumInsured: vehicleData?.sumInsured
    };
    const check = validateKenyanInsuredPhone(insuredData.phoneNumber, vehicleCtx);
    if (check.valid) {
      setMobileNumber(smartFormatPhoneNumber(check.storageFormat));
    }
  }, [insuredData, vehicleData, mobileNumber]);

  useEffect(() => {
    setRectificationState(prev => ({
      ...prev,
      emailAddress: insuredData?.emailAddress || insuredData?.email || prev.emailAddress || '',
      startDate: startDate || prev.startDate || ''
    }));
  }, [insuredData?.emailAddress, insuredData?.email, startDate]);

  const insuredPhoneForDisplay = insuredData?.phoneNumber || '';
  const insuredPhoneCheck = validateKenyanInsuredPhone(insuredPhoneForDisplay, {
    vehicleValue: vehicleData?.vehicleValue,
    sumInsured: vehicleData?.sumInsured
  });
  const insuredPhoneNeedsMpesa = !insuredPhoneCheck.valid;

  const buildInsuredDataForSave = (rawInsuredData = insuredData) =>
    withInsuredPhoneFromMobile(rawInsuredData, mobileNumber, vehicleData);

  const fieldOrder = ['mobileNumber'];
  
  const isFieldValid = (fieldName, value) => {
    if (!value || value.toString().trim() === '') return false;

    switch (fieldName) {
      case 'mobileNumber':
        return isValidMobileNumber();
      default:
        return false;
    }
  };

  const { 
    initializeFieldStates, 
    updateFieldValidationState,
    handleFocus,
    scrollToTop
  } = useAutoScroll(fieldOrder, isFieldValid);

  // Initialize field states and scroll to top on component mount
  useEffect(() => {
    initializeFieldStates(fieldOrder);
    scrollToTop(); // Scroll to top when user enters this step
  }, [initializeFieldStates, scrollToTop]);

  // Restore certificate-failed state from sessionStorage so retry button persists after refresh
  const CERT_FAILED_KEY = 'agentbima_cert_failed';
  const CERT_FAILED_MAX_AGE_MS = 24 * 60 * 60 * 1000; // 24h
  useEffect(() => {
    if (typeof sessionStorage === 'undefined') return;
    try {
      const raw = sessionStorage.getItem(CERT_FAILED_KEY);
      if (!raw) return;
      const stored = JSON.parse(raw);
      if (!stored || !stored.clientId || !stored.ts) return;
      if (!stored.transactionKey || stored.transactionKey !== checkoutTransactionKey) {
        sessionStorage.removeItem(CERT_FAILED_KEY);
        return;
      }
      if (Date.now() - stored.ts > CERT_FAILED_MAX_AGE_MS) {
        sessionStorage.removeItem(CERT_FAILED_KEY);
        return;
      }
      setClientId(stored.clientId);
      setPaymentStep('certificate-failed');
      setDmvicCertificate(prev => ({
        ...prev,
        error: stored.error || 'Certificate issuance failed.',
        failedJobId: stored.failedJobId || null,
        certificateJobId: stored.certificateJobId || null,
        canRetry: true,
        isIssuing: false,
        certificateNumber: null
      }));
      if (stored.referenceNumber) {
        try {
          localStorage.setItem('agentbima_payment_reference', stored.referenceNumber);
        } catch (_) {}
      }
    } catch (_) {}
  }, [checkoutTransactionKey]);

  const persistCertFailedState = (cid, { failedJobId, certificateJobId, errorMessage } = {}) => {
    try {
      const ref = typeof localStorage !== 'undefined' && (localStorage.getItem('agentbima_payment_reference') || localStorage.getItem('bimabot_payment_reference'));
      if (typeof sessionStorage !== 'undefined' && cid) {
        sessionStorage.setItem(CERT_FAILED_KEY, JSON.stringify({
          clientId: cid,
          transactionKey: checkoutTransactionKey,
          failedJobId: failedJobId != null ? failedJobId : null,
          certificateJobId: certificateJobId != null ? certificateJobId : null,
          error: errorMessage || null,
          referenceNumber: ref || null,
          ts: Date.now()
        }));
      }
    } catch (_) {}
  };
  const clearCertFailedState = () => {
    try {
      if (typeof sessionStorage !== 'undefined') sessionStorage.removeItem(CERT_FAILED_KEY);
    } catch (_) {}
  };

  const lastCheckoutTransactionKeyRef = useRef(checkoutTransactionKey);
  useEffect(() => {
    if (lastCheckoutTransactionKeyRef.current === checkoutTransactionKey) return;
    lastCheckoutTransactionKeyRef.current = checkoutTransactionKey;

    if (certificateJobPollRef.current) {
      clearInterval(certificateJobPollRef.current);
      certificateJobPollRef.current = null;
    }
    if (issuingClockRef.current) {
      clearInterval(issuingClockRef.current);
      issuingClockRef.current = null;
    }
    if (issuingVisibilityCleanupRef.current) {
      issuingVisibilityCleanupRef.current();
      issuingVisibilityCleanupRef.current = null;
    }
    if (reserveRetryTimeoutRef.current) {
      clearTimeout(reserveRetryTimeoutRef.current);
      reserveRetryTimeoutRef.current = null;
    }
    if (saveRetryTimeoutRef.current) {
      clearTimeout(saveRetryTimeoutRef.current);
      saveRetryTimeoutRef.current = null;
    }
    certificateIssuedRef.current = false;

    // Fresh transaction: clear all transient checkout state to avoid stale banners/status.
    setPaymentStep('input');
    setIsProcessing(false);
    setShowFlash(false);
    setFlashMessage('');
    setFlashType('info');
    setShowSnackbar(false);
    setSnackbarMessage('');
    setSnackbarType('info');
    setSnackbarDuration(5000);
    setLastPaymentError(null);
    setHasProceededToReceipt(false);
    setClientId(null);
    setPolicyNumber(null);
    setReserveState({ status: 'idle', error: null, retryCount: 0 });
    setDmvicCertificate(createInitialDmvicCertificateState());
    setRectificationState({
      emailAddress: insuredData?.emailAddress || insuredData?.email || '',
      startDate: startDate || '',
      suggestedStartDate: null,
      existingPolicyEndDate: null,
      existingPolicies: [],
      suggestionError: null,
      loadingSuggestion: false,
      applyError: null
    });
    doubleInsuranceFetchKeyRef.current = null;
    setIsValidatingCertificate(false);
    setCertificateValidated(false);
    reserveCoverCalledRef.current = false;
    lastReservedTermsRef.current = null;
    clearCertFailedState();
  }, [checkoutTransactionKey, insuredData?.emailAddress, insuredData?.email, startDate]);

  const handleRetryReserveCover = () => {
    reserveCoverCalledRef.current = false;
    setReserveState({ status: 'idle', error: null, retryCount: 0 });
    setReserveRetryNonce((v) => v + 1);
  };

  // Extension checkout uses the portal client from seed (seedClientId) for reserve-first payment — same row as the policy being extended.
  useEffect(() => {
    const cid = extensionContext?.seedClientId;
    if (cid != null && String(cid).trim() !== '') {
      setClientId((prev) => prev || String(cid).trim());
    }
  }, [extensionContext?.seedClientId]);

  // Reserve cover after double-insurance: allocate policy number and draft client so we show cover summary and mitigate post-payment failures.
  // IMPORTANT: only skip reserve for true extension of an existing policy (extensionContext.policyNo present).
  useEffect(() => {
    let cancelled = false;
    const RESERVE_RETRY_DELAYS_MS = [1500, 4000, 8000];
    const MAX_RESERVE_RETRIES = RESERVE_RETRY_DELAYS_MS.length;

    if (extensionContext?.policyNo) {
      setPolicyNumber(extensionContext.policyNo);
      setReserveState({ status: 'ready', error: null, retryCount: 0 });
      return;
    }
    const reserveEndYmd =
      (submissionEndDate && String(submissionEndDate).slice(0, 10)) ||
      (endDate && String(endDate).slice(0, 10)) ||
      '';
    if (
      !vehicleData?.registrationNumber ||
      !insuredData?.idNumber ||
      !startDate ||
      !reserveEndYmd ||
      !payableAmount ||
      !submissionCoverPeriod ||
      !isTpoCoverPeriodChosen(coverType, coverPeriod)
    ) {
      return;
    }

    const termsKey = [
      submissionCoverPeriod,
      startDate.slice(0, 10),
      reserveEndYmd,
      payableAmount,
      selectedClass,
      coverType
    ].join('|');

    if (clientId && lastReservedTermsRef.current === termsKey) {
      setReserveState(prev => (prev.status === 'ready' ? prev : { status: 'ready', error: null, retryCount: 0 }));
      return;
    }
    if (clientId && lastReservedTermsRef.current !== termsKey) {
      reserveCoverCalledRef.current = false;
    }
    if (reserveCoverCalledRef.current) return;

    const attemptReserveCover = async (retryCount = 0) => {
      if (cancelled) return;
      reserveCoverCalledRef.current = true;
      setReserveState({
        status: retryCount === 0 ? 'reserving' : 'retrying',
        error: null,
        retryCount
      });

      const payload = mergeAgentContextIntoRequestBody(
        {
          vehicleDetails: vehicleDataForSave,
          insuredData: buildInsuredDataForSave(),
          coverType: coverType || 'TPO',
          vehicleClass: selectedClass || 'Motor Private',
          institutionType: resolvedInstitutionType,
          startDate: startDate.slice(0, 10),
          endDate: reserveEndYmd,
          coverPeriod: submissionCoverPeriod,
          premiumAmount: payableAmount,
          pricingMode,
          commissionAllowed,
          selectedNetAmount: isPricingModeEnabledClass ? selectedNetAmount : null,
          selectedGrossAmount: isPricingModeEnabledClass ? selectedGrossAmount : null,
          pricingSource,
          ...(renewalContext?.sourceClientId
            ? {
                isRenewal: true,
                renewedFromClientId: renewalContext.sourceClientId,
                renewedFromPolicyNumber: renewalContext.sourcePolicyNumber || null
              }
            : {})
        },
        resolvedAgentTransactionContext
      );
      if (!resolvedAgentTransactionContext) {
        if (!isDirectBusiness && agentId != null && agentId !== '') payload.agentId = agentId;
        if (isDirectBusiness) {
          payload.isDirectBusiness = true;
          payload.commissionAllowed = 'No';
          payload.agency = { shortDescription: '', taxPin: '' };
        } else if (agency && typeof agency === 'object') {
          payload.agency = agency;
        }
        if (agentUserId != null && agentUserId !== '') payload.agentUserId = agentUserId;
      }

      try {
        const response = await fetch(API_ENDPOINTS.reserveCover, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        });
        const data = await response.json().catch(() => ({}));

        if (response.ok && data.success && data.clientId) {
          if (cancelled) return;
          setClientId(data.clientId);
          if (data.policyNumber) setPolicyNumber(data.policyNumber);
          lastReservedTermsRef.current = termsKey;
          setReserveState({ status: 'ready', error: null, retryCount });
          logEvent('reserve_cover_ok', { clientId: data.clientId, policyNumber: data.policyNumber });
          return;
        }

        const reserveError = data.error || `Could not reserve cover (HTTP ${response.status}).`;
        throw new Error(reserveError);
      } catch (err) {
        const reserveError = err?.message || 'Could not reserve cover right now.';
        logEvent('reserve_cover_failed', { error: reserveError, retryCount });
        if (retryCount < MAX_RESERVE_RETRIES) {
          reserveCoverCalledRef.current = false;
          if (cancelled) return;
          const nextRetry = retryCount + 1;
          setReserveState({ status: 'retrying', error: reserveError, retryCount: nextRetry });
          reserveRetryTimeoutRef.current = setTimeout(() => {
            attemptReserveCover(nextRetry);
          }, RESERVE_RETRY_DELAYS_MS[retryCount]);
          return;
        }
        reserveCoverCalledRef.current = false;
        if (cancelled) return;
        setReserveState({ status: 'failed', error: reserveError, retryCount });
        console.warn('Reserve cover failed:', err);
      }
    };

    attemptReserveCover(0);
    return () => {
      cancelled = true;
      if (reserveRetryTimeoutRef.current) {
        clearTimeout(reserveRetryTimeoutRef.current);
        reserveRetryTimeoutRef.current = null;
      }
    };
  }, [
    vehicleData, insuredData, startDate, endDate, submissionCoverPeriod, submissionEndDate, coverType, selectedClass, coverPeriod, payableAmount,
    agentId, agency, resolvedAgentTransactionContext, extensionContext, clientId, reserveRetryNonce,
    pricingMode, commissionAllowed, selectedNetAmount, selectedGrossAmount, pricingSource, isPricingModeEnabledClass, isDirectBusiness
  ]);

  // CRITICAL: Only show receipt after certificate is successfully issued
  // Watch for certificate success and proceed to receipt
  // Open receipt in a new tab (never navigate away from checkout on this page)
  const goToReceipt = () => {
    if (clientId && policyNumber) {
      setHasProceededToReceipt(true);
      logEvent('view_receipt_click', { clientId, policyNumber });
      const origin = typeof window !== 'undefined' ? window.location.origin : '';
      const params = new URLSearchParams({ view: 'receipt', clientId, policyNumber });
      const receiptUrl = `${origin}/insurance?${params.toString()}`;
      window.open(receiptUrl, '_blank', 'noopener,noreferrer');
    }
  };

  // Clear certificate job polling when leaving checkout
  useEffect(() => {
    return () => {
      if (certificateJobPollRef.current) {
        clearInterval(certificateJobPollRef.current);
        certificateJobPollRef.current = null;
      }
      if (issuingClockRef.current) {
        clearInterval(issuingClockRef.current);
        issuingClockRef.current = null;
      }
      if (issuingVisibilityCleanupRef.current) {
        issuingVisibilityCleanupRef.current();
        issuingVisibilityCleanupRef.current = null;
      }
      if (saveRetryTimeoutRef.current) {
        clearTimeout(saveRetryTimeoutRef.current);
        saveRetryTimeoutRef.current = null;
      }
    };
  }, []);

  useEffect(() => {
    const issued = !!String(dmvicCertificate.certificateNumber || '').trim();
    const afterPayment = paymentStep === 'success';
    certificateIssuedRef.current = issued && afterPayment;
    if (!certificateIssuedRef.current) return;
    if (saveRetryTimeoutRef.current) {
      clearTimeout(saveRetryTimeoutRef.current);
      saveRetryTimeoutRef.current = null;
    }
    try {
      localStorage.removeItem('bimabot_pending_application');
    } catch (_) { /* ignore */ }
    if (isPendingPolicySaveToast(snackbarMessage)) {
      setShowSnackbar(false);
    }
  }, [dmvicCertificate.certificateNumber, paymentStep, snackbarMessage]);

  const handleMobileNumberChange = (e) => {
    let value = e.target.value;
    
    // Allow numbers, +, spaces, and dashes (for user-friendly formatting)
    value = value.replace(/[^0-9+\s\-]/g, '');
    
    // Auto-format on blur or when user pauses typing
    setMobileNumber(value);
    
    // Trigger auto-scroll validation
    updateFieldValidationState('mobileNumber', value);
    
    // Auto-scroll to payment card when mobile number is valid
    const cleanNumber = value.replace(/[^0-9]/g, '');
    const isValid = cleanNumber.length >= 9 && cleanNumber.length <= 12;
    
    if (isValid) {
      setTimeout(() => {
        const proceedButton = document.getElementById('proceedToPayButton');
        if (proceedButton) {
          proceedButton.scrollIntoView({ 
            behavior: 'smooth', 
            block: 'center',
            inline: 'nearest'
          });
          
          // Add visual highlight effect
          proceedButton.style.transition = 'all 0.3s ease';
          proceedButton.style.boxShadow = '0 0 0 3px rgba(16, 185, 129, 0.3)';
          
          // Remove highlight after a delay
          setTimeout(() => {
            proceedButton.style.boxShadow = '';
          }, 2000);
        }
      }, 300);
    }
  };

  // Auto-format on blur (when user finishes typing)
  const handleMobileNumberBlur = () => {
    const formatted = smartFormatPhoneNumber(mobileNumber);
    if (formatted && formatted !== mobileNumber) {
      setMobileNumber(formatted);
    }
  };

  const openCertificatePdf = (url, filename) => {
    const link = document.createElement('a');
    link.href = url;
    link.target = '_blank';
    link.download = filename || 'Certificate.pdf';
    link.rel = 'noopener noreferrer';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.open(url, '_blank', 'noopener,noreferrer');
  };

  const prefetchCertificateUrl = (certificateNumber) => {
    const certNum = String(certificateNumber || '').trim();
    if (!certNum) return;
    fetch(API_ENDPOINTS.getCertificateUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ certificateNumber: certNum })
    })
      .then((res) => res.json().catch(() => ({})))
      .then((result) => {
        if (!result || !result.success || !result.certificateUrl) return;
        setDmvicCertificate((prev) => {
          if (String(prev.certificateNumber || '').trim() !== certNum) return prev;
          if (prev.certificateUrl) return prev;
          return { ...prev, certificateUrl: result.certificateUrl };
        });
      })
      .catch(() => {});
  };

  // One-click download: use a URL already fetched for this cert number; otherwise resolve via DMVIC.
  const handleDownloadCertificate = () => {
    const certNum = dmvicCertificate.certificateNumber;
    if (certNum && dmvicCertificate.certificateUrl) {
      openCertificatePdf(dmvicCertificate.certificateUrl, `Certificate_${certNum}.pdf`);
      showNotification('✅ Certificate download started.', 'success', 4000);
      logEvent('download_certificate_ok', { certificateNumber: certNum, fromUrl: true });
      return;
    }
    if (certNum) {
      downloadCertificate(certNum);
      return;
    }
    if (dmvicCertificate.certificateUrl) {
      openCertificatePdf(dmvicCertificate.certificateUrl, 'Certificate.pdf');
      showNotification('✅ Certificate download started.', 'success', 4000);
      logEvent('download_certificate_ok', { certificateNumber: null, fromUrl: true });
      return;
    }
    showNotification('Download URL not available. Please try again or check your email.', 'warning', 4000);
  };

  // Function to download certificate PDF (fetches URL from API then opens/downloads)
  const downloadCertificate = async (certificateNumber, showLoadingNotification = true) => {
    try {
      if (!certificateNumber) {
        throw new Error('Certificate number is required');
      }
      logEvent('download_certificate_start', { certificateNumber });

      if (showLoadingNotification) {
        showNotification('Validating...', 'info', 2000);
      }
      
      
      const response = await fetch(API_ENDPOINTS.getCertificateUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ certificateNumber })
      });

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      
        if (result.success && result.certificateUrl) {
        const certificateUrl = result.certificateUrl;
        setDmvicCertificate((prev) => (
          String(prev.certificateNumber || '').trim() === String(certificateNumber).trim()
            ? { ...prev, certificateUrl }
            : prev
        ));
        openCertificatePdf(certificateUrl, `Certificate_${certificateNumber}.pdf`);
        logEvent('download_certificate_ok', { certificateNumber });
        showNotification(`✅ Certificate downloaded! (${certificateNumber})`, 'success', 4000);
      } else {
        const errorMsg = result.error || 'Failed to get certificate URL from DMVIC';
        console.error('❌ Certificate download failed:', errorMsg, result);
        logEvent('download_certificate_failed', { certificateNumber, error: errorMsg });
        throw new Error(errorMsg);
      }
    } catch (error) {
      console.error('❌ Certificate download error:', error);
      logEvent('download_certificate_error', { certificateNumber: certificateNumber || null, error: error?.message || String(error) });
      showNotification(`❌ Certificate download failed: ${error.message}. Please try again or contact support.`, 'error', 5000);
      
      // Return error for caller to handle if needed
      return { success: false, error: error.message };
    }
  };

  // Function to issue DMVIC certificate with retry logic
  // overrideClientId: when provided (e.g. after save-after-payment), use it so cert is issued for the just-created client
  const issueDMVICCertificate = async (isRetry = false, retryCount = 0, overrideClientId = null) => {
    setDmvicCertificate({ ...dmvicCertificate, isIssuing: true, error: null, errorCode: null, errorText: null });

    try {
      const finalClientId = overrideClientId || clientId || `AGENTBIMA-${Date.now()}`;
      
      // Get reference number from payment if available
      const paymentRef = localStorage.getItem('agentbima_payment_reference') || localStorage.getItem('bimabot_payment_reference');
      
      const applicationData = {
        clientId: finalClientId,
        insuredData: buildInsuredDataForSave(),
        vehicleDetails: vehicleDataForSave,
        // Keep TPO_EXTEND identity for DMVIC normalize / second-half (do not coerce to TPO here).
        coverType: coverType === 'TPO_EXTEND' ? 'TPO_EXTEND' : submissionCoverType,
        vehicleClass: selectedClass,
        institutionType: resolvedInstitutionType,
        startDate,
        endDate: submissionEndDate,
        coverPeriod: submissionCoverPeriod,
        premiumAmount: payableAmount,
        pricingMode,
        commissionAllowed,
        selectedNetAmount: isPricingModeEnabledClass ? selectedNetAmount : null,
        selectedGrossAmount: isPricingModeEnabledClass ? selectedGrossAmount : null,
        pricingSource,
        referenceNumber: paymentRef,
        paymentReferenceNumber: paymentRef,
        ...(policyNumber ? { policyNumber } : {}),
        ...(renewalContext?.sourceClientId
          ? {
              isRenewal: true,
              renewedFromClientId: renewalContext.sourceClientId,
              renewedFromPolicyNumber: renewalContext.sourcePolicyNumber
            }
          : {}),
        ...(coverType === 'TPO_EXTEND'
          ? {
              productVariant: 'TPO_EXTEND',
              ...(extensionContext?.policyNo
                ? {
                    policyNumber: extensionContext.policyNo,
                    extensionMode: true,
                    extensionPolicyNo: extensionContext.policyNo,
                    extensionEffectiveDate: startDate,
                    extensionToDate: submissionEndDate,
                    commissionAllowed: 'No'
                  }
                : { extensionMode: false })
            }
          : {})
      };

      // Increased timeout to 100 seconds to allow for vehicle search + certificate issuance + buffer
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 100000); // 100 second timeout
      
      let response;
      try {
        response = await fetch(API_ENDPOINTS.issueCertificate, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(applicationData),
          signal: controller.signal
        });
        clearTimeout(timeoutId);
      } catch (fetchError) {
        clearTimeout(timeoutId);
        if (fetchError.name === 'AbortError') {
          throw new Error('Request timeout - certificate issuance is taking longer than expected. Please check status or try again.');
        }
        throw fetchError;
      }

      if (!response.ok) {
        // Handle non-200 responses
        let errorText = `HTTP ${response.status}: ${response.statusText}`;
        try {
          const errorData = await response.json();
          errorText = errorData.message || errorData.error || errorText;
        } catch (e) {
          // If response is not JSON, use status text
        }
        throw new Error(errorText);
      }

      const result = await response.json();

      // Post-payment queued issuance: poll job status until completed/failed
      if (result.status === 'queued' && result.jobId) {
        setDmvicCertificate(prev => ({ ...prev, isIssuing: true, certificateJobId: result.jobId, error: null }));
        showNotification('Certificate queued. Waiting for DMVIC...', 'info', 5000);
        const clientIdForPoll = overrideClientId || clientId;
        startCertificateJobPolling(result.jobId, clientIdForPoll);
        return;
      }
      
      if (result.success) {
        // DMVIC can return { success: true, issuanceRequestID, needsConfirmation } without a certificate.
        // Treat that as "pending confirmation", not "issued".
        if (!result.certificateNumber && (result.pendingConfirmation || result.needsConfirmation) && result.issuanceRequestID) {
          setDmvicCertificate(prev => ({
            ...prev,
            isIssuing: false,
            certificateNumber: null,
            transactionNumber: null,
            error: result.message || 'Certificate issuance pending confirmation',
            retryCount: 0,
            queuedForRetry: false,
            issuanceRequestID: result.issuanceRequestID,
            pendingConfirmation: true
          }));
          showNotification('⏳ Certificate issuance pending confirmation. Click "Confirm & Proceed" to continue.', 'warning', 10000);
          return;
        }

        if (result.certificateNumber) certificateIssuedRef.current = true;
        setDmvicCertificate({
          isIssuing: false,
          certificateNumber: result.certificateNumber,
          transactionNumber: result.transactionNumber,
          certificateUrl: result.certificateUrl || null,
          error: null,
          retryCount: 0,
          queuedForRetry: false,
          issuanceRequestID: null,
          pendingConfirmation: false
        });

        // Show success message (use backend message when certificate was already issued to avoid "double insurance" confusion)
        const successMsg = result.alreadyIssued
          ? (result.message || 'Your certificate was already issued. Your cover is active.')
          : isRetry
            ? `Certificate issued successfully on retry: ${result.certificateNumber}`
            : `DMVIC Certificate issued successfully: ${result.certificateNumber}`;
        showNotification(successMsg, 'success');
        logEvent('certificate_issued', { certificateNumber: result.certificateNumber, clientId: finalClientId, transactionNumber: result.transactionNumber, alreadyIssued: !!result.alreadyIssued });

        // No auto-download: user can click "Download Certificate" to avoid popup blockers and failed downloads hiding the success state
        // Certificate issued successfully - useEffect will handle showing receipt
      } else {
        // Check for specific error codes that need special handling
        const errorCode = result.errors?.[0]?.errorCode || result.errorCode;
        const errorText = result.errors?.[0]?.errorText || result.message || result.error || 'Certificate issuance failed';
        
        // ER007/ER002: Data Validation Warnings - AUTO-CONFIRM intelligently
        // These are warnings, not blockers - auto-confirm with flags set to true
        // ER005: Double Insurance - BLOCKED (hard error, show to user)
        if (errorCode === 'ER007' || errorCode === 'ER002') {
          // Backend returns IssuanceRequestID - auto-confirm immediately
          // Check if needs confirmation (broker-APIS approach)
          if (result.needsConfirmation && result.issuanceRequestID) {
            const issuanceRequestID = result.response?.callbackObj?.IssuanceRequestID || result.issuanceRequestID;
            
            // Auto-confirm warnings intelligently - don't show to users
            // Immediately call handleConfirmAndProceed to auto-confirm
            // This will use IsApproved: true, IsLogBookVerified: true, IsVehicleInspected: true
            setTimeout(async () => {
              await handleConfirmAndProceed(retryCount, issuanceRequestID);
            }, 500); // Small delay to ensure state is updated
            
            // Show a simple loading message instead of scary warnings
            setDmvicCertificate({
              isIssuing: true, // Show loading state
              certificateNumber: null,
              transactionNumber: null,
              error: null, // Don't show error to user
              retryCount: retryCount,
              queuedForRetry: false,
              issuanceRequestID: issuanceRequestID,
              pendingConfirmation: false // Auto-confirming, not pending
            });
            
            showNotification('⏳ Processing certificate issuance...', 'info', 3000);
            return; // Don't show warnings to user
          }
          
          // Fallback: If issuanceRequestID exists but needsConfirmation is false, still auto-confirm
          if (result.issuanceRequestID) {
            const issuanceRequestID = result.issuanceRequestID;
            
            // Auto-confirm immediately
            setTimeout(async () => {
              await handleConfirmAndProceed(retryCount, issuanceRequestID);
            }, 500);
            
            setDmvicCertificate({
              isIssuing: true,
              certificateNumber: null,
              transactionNumber: null,
              error: null,
              retryCount: retryCount,
              queuedForRetry: false,
              issuanceRequestID: issuanceRequestID,
              pendingConfirmation: false
            });
            
            showNotification('⏳ Processing certificate issuance...', 'info', 3000);
            return;
          }
          
          // Last fallback: If no issuanceRequestID, log error but don't show scary warnings
          console.warn('⚠️ ER007/ER002 warning but no issuanceRequestID found', {
            errorCode,
            result
          });
          
          setDmvicCertificate({
            isIssuing: false,
            certificateNumber: null,
            transactionNumber: null,
            error: 'Processing certificate issuance. Please wait...',
            retryCount: retryCount,
            queuedForRetry: false,
            issuanceRequestID: null,
            pendingConfirmation: false
          });
          
          showNotification('⏳ Processing certificate issuance...', 'info', 3000);
          return;
        }
        
        // ERR10001: NTSA Data Mismatch - show persistent snackbar with detailed comparison
        if (errorCode === 'ERR10001') {
          const ntsaData = result.response?.callbackObj?.NTSAResponse;
          const userInputs = result.response?.Inputs ? JSON.parse(result.response.Inputs) : null;
          
          let errorMessage = `❌ NTSA Data Mismatch Error (ERR10001)\n\n`;
          errorMessage += `The vehicle details you entered do not match the official NTSA records.\n\n`;
          
          if (ntsaData) {
            errorMessage += `📋 NTSA Official Records:\n`;
            errorMessage += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
            if (ntsaData.VehicleRegistrationNumber) errorMessage += `Registration: ${ntsaData.VehicleRegistrationNumber}\n`;
            if (ntsaData.ChassisNumber) errorMessage += `Chassis: ${ntsaData.ChassisNumber}\n`;
            if (ntsaData.VehicleMake) errorMessage += `Make: ${ntsaData.VehicleMake}\n`;
            if (ntsaData.VehicleModel) errorMessage += `Model: ${ntsaData.VehicleModel}\n`;
            if (ntsaData.BodyType) errorMessage += `Body Type: ${ntsaData.BodyType}\n`;
            if (ntsaData.EngineNumber) errorMessage += `Engine Number: ${ntsaData.EngineNumber}\n`;
            if (ntsaData.VehicleRegistrationYear) errorMessage += `Registration Year: ${ntsaData.VehicleRegistrationYear}\n`;
            
            errorMessage += `\n`;
            
            // Show what user entered for comparison
            if (userInputs) {
              errorMessage += `📝 What You Entered:\n`;
              errorMessage += `━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n`;
              if (userInputs.registrationnumber) errorMessage += `Registration: ${userInputs.registrationnumber}\n`;
              if (userInputs.chassisnumber) errorMessage += `Chassis: ${userInputs.chassisnumber}\n`;
              if (userInputs.vehiclemake) errorMessage += `Make: ${userInputs.vehiclemake}\n`;
              if (userInputs.vehiclemodel) errorMessage += `Model: ${userInputs.vehiclemodel}\n`;
              if (userInputs.bodytype) errorMessage += `Body Type: ${userInputs.bodytype}\n`;
              if (userInputs.enginenumber) errorMessage += `Engine Number: ${userInputs.enginenumber}\n`;
              if (userInputs.yearofregistration) errorMessage += `Registration Year: ${userInputs.yearofregistration}\n`;
              
              errorMessage += `\n`;
            }
            
            // Highlight differences
            errorMessage += `⚠️ Please update your vehicle details to match the NTSA records above.\n\n`;
            errorMessage += `Need help? Contact our support team:\n`;
            errorMessage += `📧 Email: requsts@monaarchinsurance.co.ke\n`;
            errorMessage += `💬 WhatsApp: Click here to chat\n`;
            errorMessage += `   https://wa.me/254738669931?text=hello`;
          } else {
            errorMessage += `Please verify your vehicle details match NTSA records.\n\n`;
            errorMessage += `Need help? Contact our support team:\n`;
            errorMessage += `📧 Email: requsts@monaarchinsurance.co.ke\n`;
            errorMessage += `💬 WhatsApp: Click here to chat\n`;
            errorMessage += `   https://wa.me/254738669931?text=hello`;
          }
          
          setDmvicCertificate({
            isIssuing: false,
            certificateNumber: null,
            transactionNumber: null,
            error: errorText,
            retryCount: retryCount,
            queuedForRetry: false
          });
          
          // Show persistent snackbar (no timeout) with close button
          showPersistentNotification(errorMessage, 'error');
          
          return; // Don't queue for retry on NTSA mismatch
        }
        
        // Check if retry is queued
        if (result.queuedForRetry) {
          setDmvicCertificate({
            isIssuing: false,
            certificateNumber: null,
            transactionNumber: null,
            error: null,
            retryCount: result.retryCount || 0,
            maxRetries: result.maxRetries || 5,
            queuedForRetry: true,
            nextRetryAt: result.nextRetryAt
          });
          
          showNotification(
            `Certificate issuance failed but has been queued for automatic retry (${result.retryCount}/${result.maxRetries}). We'll keep trying in the background.`,
            'warning'
          );
          
          // Start polling for certificate status
          startCertificateStatusPolling(finalClientId);
        } else {
          // No retry queued - show error and allow manual retry; include exact error for rectification
          const displayError = result.exactError || result.message || result.error || 'Certificate issuance failed';
          setDmvicCertificate({
            isIssuing: false,
            certificateNumber: null,
            transactionNumber: null,
            error: displayError,
            errorCode: result.errorCode ?? null,
            errorText: result.errorText ?? null,
            retryCount: retryCount,
            queuedForRetry: false
          });
          
          const errorMsg = result.exactError || result.message || result.error || 'Certificate issuance failed';
          showNotification(
            `Certificate issuance failed: ${errorMsg}. You can try again manually.`,
            'error'
          );
        }
      }
    } catch (error) {
      setDmvicCertificate({
        isIssuing: false,
        certificateNumber: null,
        transactionNumber: null,
        error: error.message,
        errorCode: null,
        errorText: null,
        retryCount: retryCount,
        queuedForRetry: false
      });
      
      showNotification(
        `Certificate issuance failed: ${error.message}. You can try again manually.`,
        'error'
      );
    }
  };

  const stopCertificateJobPolling = () => {
    if (certificateJobPollRef.current) {
      clearInterval(certificateJobPollRef.current);
      certificateJobPollRef.current = null;
    }
    if (issuingClockRef.current) {
      clearInterval(issuingClockRef.current);
      issuingClockRef.current = null;
    }
    if (issuingVisibilityCleanupRef.current) {
      issuingVisibilityCleanupRef.current();
      issuingVisibilityCleanupRef.current = null;
    }
  };

  // Shared handling for GET /api/certificate-status/:jobId (used by the poller and manual "retry" preflight).
  const applyCertificateReady = (data, jobId) => {
    if (!isCertificateDownloadReady(data) && !(data && data.state === 'completed' && !(data.pendingConfirmation || data.needsConfirmation))) {
      if (data && (data.pendingConfirmation || data.needsConfirmation) && data.issuanceRequestID) {
        stopCertificateJobPolling();
        setDmvicCertificate((prev) => ({
          ...prev,
          isIssuing: false,
          certificateNumber: null,
          transactionNumber: null,
          error: data.message || 'Certificate issuance pending confirmation',
          retryCount: 0,
          queuedForRetry: false,
          issuanceRequestID: data.issuanceRequestID,
          pendingConfirmation: true,
          failedJobId: null,
          certificateJobId: null,
          stillWaiting: false,
          recordingOnCore: false
        }));
        showNotification('⏳ Certificate issuance pending confirmation. Click "Confirm & Proceed" to continue.', 'warning', 10000);
      }
      return;
    }
    stopCertificateJobPolling();
    const certNumber = data.certificateNumber || null;
    if (!certNumber && (data.pendingConfirmation || data.needsConfirmation) && data.issuanceRequestID) {
      setDmvicCertificate((prev) => ({
        ...prev,
        isIssuing: false,
        certificateNumber: null,
        transactionNumber: null,
        error: data.message || 'Certificate issuance pending confirmation',
        retryCount: 0,
        queuedForRetry: false,
        issuanceRequestID: data.issuanceRequestID,
        pendingConfirmation: true,
        failedJobId: null,
        certificateJobId: null,
        stillWaiting: false,
        recordingOnCore: false
      }));
      showNotification('⏳ Certificate issuance pending confirmation. Click "Confirm & Proceed" to continue.', 'warning', 10000);
      return;
    }
    const txnNumber = data.transactionNumber || null;
    clearCertFailedState();
    if (certNumber) certificateIssuedRef.current = true;
    setDmvicCertificate({
      isIssuing: false,
      certificateNumber: certNumber,
      transactionNumber: txnNumber,
      certificateUrl: data.certificateUrl || null,
      error: null,
      retryCount: 0,
      queuedForRetry: false,
      issuanceRequestID: null,
      pendingConfirmation: false,
      failedJobId: null,
      certificateJobId: null,
      issuingElapsedMs: 0,
      recordingOnCore: !!data.recordingOnCore,
      stillWaiting: false
    });
    showNotification(
      certNumber ? `DMVIC Certificate issued successfully: ${certNumber}` : 'Certificate ready for download.',
      'success'
    );
    logEvent('certificate_job_completed', { certificateNumber: certNumber, certificateUrl: !!data.certificateUrl, jobId });
    if (certNumber && !data.certificateUrl) {
      prefetchCertificateUrl(certNumber);
    }
  };

  const applyQueueJobCompleted = (data, jobId) => {
    if (isCertificateDownloadReady(data) || (data && (data.pendingConfirmation || data.needsConfirmation))) {
      applyCertificateReady(data, jobId);
      return;
    }
    if (!data || data.state !== 'completed') return;
    applyCertificateReady(data, jobId);
  };

  const applyQueueJobFailed = (data, jobId, clientIdForFallback) => {
    if (!data || data.state !== 'failed') return;
    if (isCertificateDownloadReady(data)) {
      applyCertificateReady(data, jobId);
      return;
    }
    stopCertificateJobPolling();
    const errMsg = data.error || 'Certificate issuance failed';
    setDmvicCertificate({
      isIssuing: false,
      certificateNumber: null,
      transactionNumber: null,
      error: errMsg,
      errorCode: data.errorCode ?? null,
      errorText: data.errorText ?? errMsg,
      retryCount: 0,
      queuedForRetry: false,
      failedJobId: jobId,
      canRetry: true,
      certificateJobId: null
    });
    logEvent('certificate_failed', { jobId, error: errMsg, referenceNumber: localStorage.getItem('agentbima_payment_reference') });
    setPaymentStep('certificate-failed');
    persistCertFailedState(clientIdForFallback, { failedJobId: jobId, certificateJobId: null, errorMessage: errMsg });
    showNotification(`Certificate issuance failed: ${errMsg}. You can try again manually.`, 'error');
  };

  // Job poll: after payment, certificate issuance is queued (BullMQ). We poll GET /api/certificate-status/:jobId.
  // Download is shown as soon as downloadReady/certificateNumber is true — even if Core is still running.
  // We only set failedJobId for BullMQ state "failed" without a certificate. Slow jobs stay in a calm waiting state.
  const startCertificateJobPolling = (jobId, clientIdForFallback = null) => {
    stopCertificateJobPolling();
    let pollAttempts = 0;
    let lastKnownQueueState = null; // 'active' | 'waiting' | 'failed' | ...
    let lastPhase = null;
    let slowMode = false;
    let inFlight = false;
    let currentPollMs = 1000;
    const startedAt = Date.now();
    const fastPollMs = 1000;
    const mediumPollMs = 5000;
    const slowPollMs = 15000;

    const armTimer = (ms) => {
      currentPollMs = ms;
      if (certificateJobPollRef.current) clearInterval(certificateJobPollRef.current);
      certificateJobPollRef.current = setInterval(poll, ms);
    };

    const applyElapsed = (status = {}) => {
      if (status.phase) lastPhase = status.phase;
      if (status.state) lastKnownQueueState = status.state;
      setDmvicCertificate((prev) => {
        if (prev.certificateNumber || prev.certificateUrl) return prev;
        return {
          ...prev,
          isIssuing: true,
          certificateJobId: jobId,
          issuingElapsedMs: Date.now() - startedAt,
          issuingPhase: lastPhase || prev.issuingPhase || null,
          issuingQueueState: lastKnownQueueState || prev.issuingQueueState || null,
          stillWaiting: slowMode
        };
      });
    };

    const tryClientFallback = async () => {
      if (!clientIdForFallback) return false;
      try {
        const fallbackRes = await fetch(API_ENDPOINTS.certificateStatus(clientIdForFallback));
        const fb = await fallbackRes.json().catch(() => ({}));
        if (fallbackRes.ok && isCertificateDownloadReady(fb)) {
          applyCertificateReady({ ...fb, downloadReady: true, state: fb.state || 'issued' }, jobId);
          return true;
        }
        if (fallbackRes.ok && (fb.pendingConfirmation || fb.needsConfirmation) && fb.issuanceRequestID) {
          applyCertificateReady(fb, jobId);
          return true;
        }
      } catch (_) {
        // keep polling
      }
      return false;
    };

    const poll = async () => {
      if (inFlight) return;
      inFlight = true;
      pollAttempts += 1;
      applyElapsed();
      try {
        const response = await fetch(API_ENDPOINTS.certificateJobStatus(jobId, clientIdForFallback));
        const data = await response.json().catch(() => ({}));
        applyElapsed({ phase: data.phase, state: data.state });

        if (isCertificateDownloadReady(data)) {
          applyCertificateReady(data, jobId);
          return;
        }
        if ((data.pendingConfirmation || data.needsConfirmation) && data.issuanceRequestID) {
          applyCertificateReady(data, jobId);
          return;
        }

        if (response.status === 404 || response.status === 503) {
          if (await tryClientFallback()) return;
          if (pollAttempts >= 90 && currentPollMs === fastPollMs) armTimer(mediumPollMs);
          else if (pollAttempts >= 140 && !slowMode) {
            slowMode = true;
            armTimer(slowPollMs);
            applyElapsed();
          }
          return;
        }

        if (!response.ok) {
          if (await tryClientFallback()) return;
          if (pollAttempts >= 90 && currentPollMs === fastPollMs) armTimer(mediumPollMs);
          else if (pollAttempts >= 140 && !slowMode) {
            slowMode = true;
            armTimer(slowPollMs);
            applyElapsed();
          }
          return;
        }

        if (data.state) lastKnownQueueState = data.state;
        if (data.state === 'completed') {
          applyQueueJobCompleted(data, jobId);
          return;
        }
        if (data.state === 'failed') {
          if (await tryClientFallback()) return;
          applyQueueJobFailed(data, jobId, clientIdForFallback);
          return;
        }

        if (pollAttempts >= 90 && currentPollMs === fastPollMs) {
          armTimer(mediumPollMs);
        } else if (pollAttempts >= 140 && !slowMode) {
          slowMode = true;
          armTimer(slowPollMs);
          applyElapsed();
          const inProgress = ['active', 'waiting', 'delayed'].includes(lastKnownQueueState);
          showNotification(
            inProgress
              ? 'Still working on your certificate. You can stay here or find it on your dashboard when it is ready.'
              : 'Still checking certificate status. Download will appear here when it is ready.',
            'info',
            6000
          );
        }
      } catch (err) {
        console.error('Error polling certificate job status:', err);
        if (await tryClientFallback()) return;
        if (pollAttempts >= 90 && currentPollMs === fastPollMs) armTimer(mediumPollMs);
        else if (pollAttempts >= 140 && !slowMode) {
          slowMode = true;
          armTimer(slowPollMs);
          applyElapsed();
        }
      } finally {
        inFlight = false;
      }
    };

    issuingClockRef.current = setInterval(() => applyElapsed(), 1000);
    if (typeof document !== 'undefined') {
      const onVis = () => {
        if (document.visibilityState !== 'visible') return;
        applyElapsed();
        poll();
      };
      document.addEventListener('visibilitychange', onVis);
      issuingVisibilityCleanupRef.current = () => document.removeEventListener('visibilitychange', onVis);
    }

    poll();
    armTimer(fastPollMs);
  };

  // Poll certificate status when retry is queued
  const startCertificateStatusPolling = (clientIdToPoll) => {
    stopCertificateJobPolling();
    let pollAttempts = 0;
    const maxPollAttempts = 90;
    let inFlight = false;

    const pollOnce = async () => {
      if (inFlight) return;
      inFlight = true;
      pollAttempts++;
      try {
        const response = await fetch(API_ENDPOINTS.certificateStatus(clientIdToPoll));
        
        if (!response.ok) {
          if (response.status === 404) {
            return;
          }
          throw new Error(`HTTP ${response.status}: ${response.statusText}`);
        }
        
        const result = await response.json();
        
        if (result.success && (result.hasCertificate || isCertificateDownloadReady(result))) {
          applyCertificateReady({ ...result, downloadReady: true, state: result.certificateStatus || 'issued' }, null);
          return;
        } else if (result.certificateStatus === 'failed') {
          stopCertificateJobPolling();
          const errMsg = result.lastError || 'Certificate issuance failed after all retry attempts';
          setDmvicCertificate({
            isIssuing: false,
            certificateNumber: null,
            transactionNumber: null,
            error: errMsg,
            retryCount: result.retryCount || 0,
            queuedForRetry: false,
            failedJobId: null,
            canRetry: true
          });
          setPaymentStep('certificate-failed');
          persistCertFailedState(clientIdToPoll, { failedJobId: null, certificateJobId: null, errorMessage: errMsg });
          showNotification(
            `Payment successful, but certificate issuance failed after ${result.retryCount} attempts. Please contact support or try again manually.`,
            'error'
          );
        }
        
        if (pollAttempts === maxPollAttempts) {
          showNotification(
            'Still working on your certificate. Download will appear here when it is ready, or find it on your dashboard.',
            'info',
            6000
          );
        }
      } catch (error) {
        console.error('Error polling certificate status:', error);
      } finally {
        inFlight = false;
      }
    };

    pollOnce();
    if (certificateJobPollRef.current) clearInterval(certificateJobPollRef.current);
    certificateJobPollRef.current = setInterval(pollOnce, 1000);
  };

  const fetchSuggestedStartDateForRetry = async () => {
    setRectificationState(prev => ({ ...prev, loadingSuggestion: true, suggestionError: null }));
    const noDateMessage =
      'The next available start date is more than 30 days away. Please try again when the current policy is within 30 days of expiry, or contact D-MVIC support.';
    try {
      const token =
        typeof localStorage !== 'undefined' &&
        (localStorage.getItem('agentToken') || localStorage.getItem('employeeToken'));

      if (clientId && token) {
        const isEmployee = !!localStorage.getItem('employeeToken');
        const path = isEmployee
          ? '/api/employee/suggested-certificate-start-date'
          : '/api/agent/suggested-certificate-start-date';
        const response = await fetch(
          `${getApiUrl(path)}?clientId=${encodeURIComponent(clientId)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        const result = await response.json();
        if (!response.ok) throw new Error(result.error || 'Failed to get next available date');
        const suggestion = result.suggestedStartDate || null;
        const existingEnd =
          result.latestOtherPolicyEndDate ||
          latestPolicyEndFromList(result.existingPolicies) ||
          null;
        setRectificationState(prev => ({
          ...prev,
          suggestedStartDate: suggestion,
          existingPolicyEndDate: existingEnd,
          existingPolicies: result.existingPolicies || [],
          loadingSuggestion: false,
          suggestionError: suggestion ? null : noDateMessage
        }));
        return suggestion;
      }

      if (!vehicleData?.registrationNumber) {
        throw new Error('Vehicle registration is required to look up the next available date.');
      }
      const response = await fetch(API_ENDPOINTS.validateDoubleInsurance, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          vehicleData: {
            registrationNumber: vehicleData.registrationNumber,
            chassisNumber: vehicleData.chassisNumber || ''
          },
          policyDates: {
            startDate: startDate,
            endDate: submissionEndDate
          }
        })
      });
      const result = await response.json();
      if (!response.ok) throw new Error(result.error || 'Failed to get next available date');
      const suggestion = result.nextAvailableDate || null;
      const existingEnd = latestPolicyEndFromList(result.existingPolicies);
      setRectificationState(prev => ({
        ...prev,
        suggestedStartDate: suggestion,
        existingPolicyEndDate: existingEnd,
        existingPolicies: result.existingPolicies || [],
        loadingSuggestion: false,
        suggestionError: suggestion ? null : noDateMessage
      }));
      return suggestion;
    } catch (err) {
      setRectificationState(prev => ({
        ...prev,
        loadingSuggestion: false,
        suggestionError: err.message || 'Failed to fetch next available date'
      }));
      return null;
    }
  };

  useEffect(() => {
    if (paymentStep !== 'certificate-failed') return;
    const isDoubleInsurance = isDoubleInsuranceFailure(
      dmvicCertificate.errorCode,
      dmvicCertificate.errorText,
      dmvicCertificate.error
    );
    if (!isDoubleInsurance) return;
    const fetchKey = `${clientId || 'no-client'}|${dmvicCertificate.errorCode || ''}|${dmvicCertificate.error || ''}`;
    if (doubleInsuranceFetchKeyRef.current === fetchKey) return;
    doubleInsuranceFetchKeyRef.current = fetchKey;
    fetchSuggestedStartDateForRetry();
  }, [
    paymentStep,
    clientId,
    dmvicCertificate.errorCode,
    dmvicCertificate.errorText,
    dmvicCertificate.error
  ]);

  // Agent/employee: queue a fresh issuance job (same as dashboard retry). Picks the correct route when the session uses employeeToken.
  const certificateRetryUrlByPortalSession = () => {
    if (typeof localStorage !== 'undefined' && localStorage.getItem('employeeToken')) {
      return getApiUrl('/api/employee/certificate-retry');
    }
    return API_ENDPOINTS.certificateRetryByClient();
  };

  // Handles certificate-retry responses: queued jobId, sync success, or already-issued idempotency (no jobId).
  const applyCertificateRetryApiResponse = (data, clientIdForPoll) => {
    if (!data || typeof data !== 'object') return false;

    if (data.jobId) {
      showNotification('Certificate retry queued, processing...', 'info', 3000);
      startCertificateJobPolling(data.jobId, clientIdForPoll);
      setDmvicCertificate((prev) => ({
        ...prev,
        failedJobId: null,
        certificateJobId: data.jobId,
        isIssuing: true,
        error: null,
        errorCode: null,
        errorText: null
      }));
      return true;
    }

    if (data.success && !data.certificateNumber && (data.pendingConfirmation || data.needsConfirmation) && data.issuanceRequestID) {
      setDmvicCertificate((prev) => ({
        ...prev,
        isIssuing: false,
        certificateNumber: null,
        transactionNumber: null,
        error: data.message || 'Certificate issuance pending confirmation',
        retryCount: 0,
        queuedForRetry: false,
        issuanceRequestID: data.issuanceRequestID,
        pendingConfirmation: true,
        failedJobId: null,
        certificateJobId: null
      }));
      showNotification('⏳ Certificate issuance pending confirmation. Click "Confirm & Proceed" to continue.', 'warning', 10000);
      return true;
    }

    if (data.success && data.certificateNumber) {
      clearCertFailedState();
      certificateIssuedRef.current = true;
      setDmvicCertificate({
        isIssuing: false,
        certificateNumber: data.certificateNumber,
        transactionNumber: data.transactionNumber || null,
        certificateUrl: data.certificateUrl || null,
        error: null,
        errorCode: null,
        errorText: null,
        retryCount: 0,
        queuedForRetry: false,
        issuanceRequestID: null,
        pendingConfirmation: false,
        failedJobId: null,
        certificateJobId: null,
        canRetry: false
      });
      setCertificateValidated(true);
      const successMsg = data.alreadyIssued
        ? (data.message || 'Your certificate was already issued. Your cover is active.')
        : `Certificate issued successfully: ${data.certificateNumber}`;
      showNotification(successMsg, 'success');
      logEvent('certificate_issued', {
        certificateNumber: data.certificateNumber,
        clientId: clientIdForPoll,
        transactionNumber: data.transactionNumber,
        alreadyIssued: !!data.alreadyIssued
      });
      return true;
    }

    return false;
  };

  // Manual retry: preflight GET so we do not call BullMQ job.retry() on an active job. Then: poll, re-queue failed job, or new job by clientId.
  const handleManualRetry = async (overrides = null) => {
    const isPlainObject = (value) => {
      if (!value || typeof value !== 'object') return false;
      const proto = Object.getPrototypeOf(value);
      return proto === Object.prototype || proto === null;
    };

    const looksLikeEvent =
      overrides &&
      typeof overrides === 'object' &&
      (typeof overrides.preventDefault === 'function' ||
        typeof overrides.stopPropagation === 'function' ||
        Object.prototype.hasOwnProperty.call(overrides, 'nativeEvent') ||
        Object.prototype.hasOwnProperty.call(overrides, 'currentTarget') ||
        Object.prototype.hasOwnProperty.call(overrides, 'target'));

    const safeOverrides = !looksLikeEvent && isPlainObject(overrides) ? overrides : null;
    const hasOverrides = !!(safeOverrides && (safeOverrides.emailAddress || safeOverrides.startDate || safeOverrides.endDate));
    const jobId = dmvicCertificate.failedJobId || dmvicCertificate.certificateJobId;
    if (jobId && !hasOverrides) {
      setDmvicCertificate((prev) => ({ ...prev, isIssuing: true, error: null, errorCode: null, errorText: null }));
      const tryNewJobByClient = async (errContext) => {
        const token =
          typeof localStorage !== 'undefined' && (localStorage.getItem('agentToken') || localStorage.getItem('employeeToken'));
        if (!clientId || !token) {
          setDmvicCertificate((prev) => ({ ...prev, isIssuing: false, error: errContext || prev.error }));
          showNotification(errContext || 'Could not re-queue. Sign in and retry from your dashboard.', 'error');
          return;
        }
        setDmvicCertificate((prev) => ({ ...prev, isIssuing: true, error: null, errorCode: null, errorText: null }));
        try {
          const body = { clientId };
          const response = await fetch(certificateRetryUrlByPortalSession(), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify(body)
          });
          const data = await response.json();
          if (!response.ok) throw new Error(data.error || 'Queue retry failed');
          if (applyCertificateRetryApiResponse(data, clientId)) return;
          throw new Error(data.error || data.message || 'Certificate retry did not return a job or certificate');
        } catch (e) {
          setDmvicCertificate((prev) => ({ ...prev, isIssuing: false, error: e.message || 'Failed to queue' }));
          showNotification(e.message || 'Retry failed', 'error');
        }
      };
      try {
        const statusRes = await fetch(API_ENDPOINTS.certificateJobStatus(jobId, clientId));
        const statusData = await statusRes.json().catch(() => ({}));
        if (statusRes.ok && statusData && (isCertificateDownloadReady(statusData) || ((statusData.pendingConfirmation || statusData.needsConfirmation) && statusData.issuanceRequestID))) {
          applyCertificateReady(statusData, jobId);
          return;
        }
        if (statusRes.ok && statusData && statusData.state) {
          const st = statusData.state;
          if (st === 'active' || st === 'waiting' || st === 'delayed') {
            showNotification('Certificate is still being processed. Monitoring…', 'info', 4000);
            startCertificateJobPolling(jobId, clientId);
            return;
          }
          if (st === 'completed') {
            applyQueueJobCompleted(statusData, jobId);
            return;
          }
          if (st === 'failed') {
            const postRes = await fetch(API_ENDPOINTS.certificateJobRetry(jobId), {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: clientId ? JSON.stringify({ clientId }) : undefined
            });
            const postData = await postRes.json().catch(() => ({}));
            if (postRes.ok) {
              showNotification(postData.message || 'Certificate re-queued, processing…', 'info', 3000);
              startCertificateJobPolling(jobId, clientId);
              return;
            }
            if (
              (postData.error && String(postData.error).toLowerCase().includes('cannot be retried')) ||
              (postData.state && ['active', 'waiting', 'delayed'].includes(postData.state))
            ) {
              showNotification('Re-attaching to the running job…', 'info', 3000);
              startCertificateJobPolling(jobId, clientId);
              return;
            }
            await tryNewJobByClient(postData.error || 'Retry not allowed. Queueing a new job…');
            return;
          }
        }
        if (!statusRes.ok) {
          await tryNewJobByClient(statusData.error || 'Could not read job status. Queueing a new certificate job…');
          return;
        }
        await tryNewJobByClient('Unrecognized job state. Queueing a new certificate job…');
      } catch (err) {
        await tryNewJobByClient(err.message || 'Could not reach status. Queueing a new certificate job…');
      }
      return;
    }
    // No jobId (e.g. restored from sessionStorage or cert failed via status polling): retry by clientId if agent
    if (clientId) {
      const token = typeof localStorage !== 'undefined' && (localStorage.getItem('agentToken') || localStorage.getItem('employeeToken'));
      if (token) {
        setDmvicCertificate((prev) => ({ ...prev, isIssuing: true, error: null, errorCode: null, errorText: null }));
        try {
          const body = { clientId, ...(safeOverrides || {}) };
          const response = await fetch(certificateRetryUrlByPortalSession(), {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify(body)
          });
          const data = await response.json();
          if (!response.ok) {
            throw new Error(data.error || 'Retry failed');
          }
          if (data?.rectification) {
            setRectificationState(prev => ({
              ...prev,
              emailAddress: data.rectification.updatedEmailAddress || prev.emailAddress,
              startDate: data.rectification.updatedStartDate || prev.startDate,
              applyError: null
            }));
          }
          if (applyCertificateRetryApiResponse(data, clientId)) return;
          throw new Error(data.error || data.message || 'Certificate retry did not return a job or certificate');
        } catch (err) {
          setDmvicCertificate(prev => ({
            ...prev,
            isIssuing: false,
            error: err.message || 'Retry failed'
          }));
          showNotification(`Retry failed: ${err.message}`, 'error');
        }
        return;
      }
    }
    const currentRetryCount = dmvicCertificate.retryCount || 0;
    await issueDMVICCertificate(true, currentRetryCount + 1);
  };

  const handleApplyRectificationAndRetry = async () => {
    const nextEmail = String(rectificationState.emailAddress || '').trim();
    const isDoubleInsurance = isDoubleInsuranceFailure(dmvicCertificate.errorCode, dmvicCertificate.errorText, dmvicCertificate.error);
    const isEmailIssue = isInvalidEmailFailure(dmvicCertificate.errorCode, dmvicCertificate.errorText, dmvicCertificate.error);

    const overrides = {};
    if (isEmailIssue) {
      if (!nextEmail) {
        setRectificationState(prev => ({ ...prev, applyError: 'Email address is required to retry issuance.' }));
        return;
      }
      if (!isValidEmail(nextEmail)) {
        setRectificationState(prev => ({ ...prev, applyError: 'Enter a valid email address (example: name@example.com).' }));
        return;
      }
      overrides.emailAddress = nextEmail;
    }

    if (isDoubleInsurance) {
      const reissueDate = String(rectificationState.suggestedStartDate || '').trim().slice(0, 10);
      if (!reissueDate) {
        setRectificationState(prev => ({
          ...prev,
          applyError: rectificationState.suggestionError || 'Next available date is not ready yet. Please wait or contact D-MVIC support.'
        }));
        return;
      }
      overrides.startDate = reissueDate;
    }

    setRectificationState(prev => ({ ...prev, applyError: null }));
    await handleManualRetry(overrides);
  };

  // Handle ER007 confirmation and proceed with certificate issuance
  // Implements automatic retry when ER002 returns a new IssuanceRequestID
  const handleConfirmAndProceed = async (retryCount = 0, currentIssuanceID = null) => {
    const issuanceRequestID = currentIssuanceID || dmvicCertificate.issuanceRequestID;
    
    if (!issuanceRequestID) {
      showNotification('No issuance request ID available. Please try again.', 'error');
      setDmvicCertificate({
        ...dmvicCertificate,
        isIssuing: false
      });
      return;
    }

    // Set confirming state
    if (retryCount === 0) {
      setDmvicCertificate({ ...dmvicCertificate, isIssuing: true, error: null, errorCode: null, errorText: null });
      showNotification('Confirming certificate issuance...', 'info');
    }

    try {
      // Call confirmation endpoint (single call, no retries - matches broker-APIS)
      const response = await fetch(API_ENDPOINTS.confirmCertificate, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          issuanceRequestID: issuanceRequestID,
          clientId: clientId || null,
          referenceNumber: localStorage.getItem('agentbima_payment_reference') || localStorage.getItem('bimabot_payment_reference') || null,
          options: {
            isApproved: true,
            // Explicitly set these flags to reduce ER002 loops during confirmation.
            isLogBookVerified: true,
            isVehicleInspected: true,
            additionalComments: retryCount > 0 
              ? `Automatic retry ${retryCount} - User acknowledged ER007 validation warnings and requested to proceed`
              : 'User acknowledged ER007 validation warnings and requested to proceed',
            userName: 'Agent BIMA User'
          }
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();

      // Extract certificate number from multiple possible locations
      // Sometimes DMVIC returns success: false but still includes certificateNumber
      // Also check errorText JSON which might contain actualCNo
      let certificateNumber = result.certificateNumber || 
                              result.data?.callbackObj?.issueCertificate?.actualCNo ||
                              result.data?.callbackObj?.CertificateNumber ||
                              result.data?.certificateNumber ||
                              result.data?.CertificateNumber ||
                              result.data?.callbackObj?.Certificate?.CertificateNumber;
      
      // If not found, check errorText JSON (sometimes DMVIC puts it there)
      if (!certificateNumber && result.errors?.[0]?.errorText) {
        try {
          const errorTextJson = typeof result.errors[0].errorText === 'string' 
            ? JSON.parse(result.errors[0].errorText) 
            : result.errors[0].errorText;
          certificateNumber = errorTextJson?.actualCNo || 
                             errorTextJson?.certificateNumber ||
                             errorTextJson?.CertificateNumber;
        } catch (e) {
          // Not JSON, ignore
        }
      }
      
      // Last resort: search in issueCertificate object values
      if (!certificateNumber && result.data?.callbackObj?.issueCertificate) {
        const issueCert = result.data.callbackObj.issueCertificate;
        certificateNumber = Object.values(issueCert).find(v => 
          typeof v === 'string' && /^C\d+$/.test(v.trim())
        );
      }

      // Check if certificate was issued (even if success flag is false)
      if (certificateNumber) {
        // Certificate issued successfully - treat as success even if success flag is false
        clearCertFailedState();
        setDmvicCertificate({
          isIssuing: false,
          certificateNumber: certificateNumber,
          transactionNumber: result.transactionNumber ||
                            result.data?.callbackObj?.Transaction?.transactionNumber ||
                            null,
          certificateUrl: result.certificateUrl || null,
          error: null,
          errorCode: null,
          errorText: null,
          retryCount: 0,
          queuedForRetry: false,
          issuanceRequestID: null,
          pendingConfirmation: false,
          canRetry: false,
          failedJobId: null,
          certificateJobId: null
        });

        // Allow payment to proceed after successful confirmation
        setCertificateValidated(true);

        const alreadyMsg = result.alreadyIssued
          ? (result.message || 'Your certificate was already issued. Your cover is active.')
          : `✅ Certificate issued successfully: ${certificateNumber}${retryCount > 0 ? ` (after ${retryCount} retry${retryCount > 1 ? 'ies' : ''})` : ''}`;
        showNotification(alreadyMsg, 'success');
        logEvent('certificate_confirmed_issued', { certificateNumber, retryCount });

        // No auto-download: user clicks "Download Certificate" so the transaction stays visible and errors are clear
      } else if (result.issuanceRequestID && result.issuanceRequestID !== issuanceRequestID && retryCount < (dmvicCertificate.maxRetries || 10)) {
        // ER002 or similar error returned a new IssuanceRequestID - automatically retry
        const maxRetries = dmvicCertificate.maxRetries || 10;
        
        // Update state with new IssuanceRequestID
        setDmvicCertificate({
          ...dmvicCertificate,
          issuanceRequestID: result.issuanceRequestID,
          retryCount: retryCount + 1,
          isIssuing: true, // Keep issuing state for retry
          error: null
        });
        
        // Wait a short delay before retrying (exponential backoff)
        const delay = Math.min(1000 * Math.pow(2, retryCount), 5000); // Max 5 seconds
        showNotification(`Retrying with new Request ID (attempt ${retryCount + 1}/${maxRetries})...`, 'info');
        await new Promise(resolve => setTimeout(resolve, delay));
        
        // Recursively retry with new IssuanceRequestID
        return handleConfirmAndProceed(retryCount + 1, result.issuanceRequestID);
      } else {
        // Confirmation failed - no new ID or max retries reached
        const maxRetries = dmvicCertificate.maxRetries || 10;
        
        // If max retries reached, queue for background retry (payment already completed)
        if (retryCount >= maxRetries && paymentStep === 'success') {
          console.warn('⚠️ Max retries reached for confirmation - payment already completed, will retry in background', {
            result,
            retryCount,
            maxRetries: maxRetries,
            issuanceRequestID: result.issuanceRequestID || issuanceRequestID
          });
          
          // Queue for background retry (no payment needed - already paid)
          setDmvicCertificate({
            ...dmvicCertificate,
            isIssuing: false,
            error: null, // Don't show error - will retry in background
            pendingConfirmation: true,
            issuanceRequestID: result.issuanceRequestID || issuanceRequestID,
            retryCount: retryCount,
            queuedForRetry: true, // Mark as queued for background retry
            canRetry: true // Allow manual retry if needed
          });
          
          showNotification(
            `Confirmation failed after ${maxRetries} attempts. Your payment was successful. We'll automatically retry the certificate confirmation in the background. You can also retry manually below.`,
            'warning',
            10000
          );
          
          // Start polling for certificate status
          if (clientId) {
            startCertificateStatusPolling(clientId);
          }
        } else {
          // Not max retries yet or payment not completed
          const errorMessage = retryCount >= maxRetries
            ? `Confirmation failed after ${maxRetries} attempts. ${result.message || 'Please try again manually.'}`
            : result.message || 'Confirmation failed. Please try again.';
          
          console.warn('⚠️ Confirmation failed:', {
            result,
            retryCount,
            maxRetries: maxRetries,
            hasNewID: result.issuanceRequestID && result.issuanceRequestID !== issuanceRequestID
          });
          
          setDmvicCertificate({
            ...dmvicCertificate,
            isIssuing: false,
            error: errorMessage,
            pendingConfirmation: result.issuanceRequestID ? true : false,
            issuanceRequestID: result.issuanceRequestID || issuanceRequestID,
            retryCount: retryCount,
            canRetry: true // Allow manual retry
          });
          
          showNotification(errorMessage, 'error');
        }
      }
    } catch (error) {
      console.error('❌ Certificate confirmation error:', error);
      
      // If we've retried multiple times, give up
      const maxRetries = dmvicCertificate.maxRetries || 10;
      if (retryCount >= maxRetries) {
        setDmvicCertificate({
          ...dmvicCertificate,
          isIssuing: false,
          error: `Confirmation failed after ${maxRetries} attempts: ${error.message}`,
          canRetry: true // Allow manual retry
        });
        showNotification(`Confirmation failed after ${maxRetries} attempts. You can try a fresh certificate issuance.`, 'error');
      } else {
        // Retry on network errors
        const delay = Math.min(1000 * Math.pow(2, retryCount), 5000);
        // Network error, retrying
        showNotification(`Network error, retrying (attempt ${retryCount + 1}/${maxRetries})...`, 'warning');
        await new Promise(resolve => setTimeout(resolve, delay));
        return handleConfirmAndProceed(retryCount + 1, issuanceRequestID);
      }
    }
  };

  // NEW: Validate certificate BEFORE payment (certificate-first flow)
  const handleValidateAndContinue = async () => {
    if (extensionSeedBlocked) {
      showNotification(EXTENSION_SEED_USER_MESSAGE, 'error', 12000);
      return;
    }
    setIsValidatingCertificate(true);
    showNotification('Validating...', 'info');
    
    try {
      // Save application to database FIRST to get clientId
      let finalClientId = clientId;
      if (!finalClientId) {
        try {
          const saveResult = await saveApplicationDataAsync({
            coverType,
            selectedClass,
            vehicleData,
            insuredData: buildInsuredDataForSave(),
            startDate,
            endDate: submissionEndDate,
            coverPeriod: submissionCoverPeriod,
            premium: premium,
            extensionPolicyNo: extensionContext?.policyNo,
            productVariant: isTpoExtendSecondHalf ? 'TPO_EXTEND' : undefined,
            extensionMode: isTpoExtendSecondHalf,
            extensionToDate: isTpoExtendSecondHalf ? submissionEndDate : undefined,
            agentTransactionContext: resolvedAgentTransactionContext,
          });
          if (saveResult?.clientId) {
            finalClientId = saveResult.clientId;
            setClientId(finalClientId);
          }
        } catch (saveError) {
          console.warn('Failed to save application before validation:', saveError);
          // Continue with temporary clientId
          finalClientId = `AGENTBIMA-${Date.now()}`;
        }
      }
      
      const applicationData = {
        clientId: finalClientId,
        insuredData: buildInsuredDataForSave(),
        vehicleDetails: vehicleDataForSave,
        coverType: submissionCoverType,
        vehicleClass: selectedClass,
        institutionType: resolvedInstitutionType,
        startDate,
        endDate: submissionEndDate,
        coverPeriod: submissionCoverPeriod,
        premiumAmount: payableAmount,
        pricingMode,
        commissionAllowed,
        selectedNetAmount: isPricingModeEnabledClass ? selectedNetAmount : null,
        selectedGrossAmount: isPricingModeEnabledClass ? selectedGrossAmount : null,
        pricingSource,
        validateBeforePayment: true, // NEW: Flag to indicate this is pre-payment validation
        ...(coverType === 'TPO_EXTEND'
          ? {
              productVariant: 'TPO_EXTEND',
              ...(extensionContext?.policyNo
                ? {
                    extensionMode: true,
                    extensionPolicyNo: extensionContext.policyNo,
                    extensionEffectiveDate: startDate,
                    extensionToDate: submissionEndDate,
                    commissionAllowed: 'No'
                  }
                : { extensionMode: false })
            }
          : {})
      };

      // Get API base URL
      const baseUrl = API_ENDPOINTS?.issueCertificate?.replace('/api/dmvic/issue-certificate', '') || 'http://127.0.0.1:5000';
      const apiUrl = `${baseUrl}/api/dmvic/issue-certificate`;
      
      // First, test connectivity with a simple health check
      try {
        const healthUrl = `${baseUrl}/health`;
        const healthResponse = await fetch(healthUrl, {
          method: 'GET',
          mode: 'cors',
          cache: 'no-cache',
          signal: AbortSignal.timeout(5000) // 5 second timeout for health check
        });
        
        if (!healthResponse.ok) {
          throw new Error(`Backend health check failed: ${healthResponse.status}`);
        }
        await healthResponse.json();
      } catch (healthError) {
        console.error('❌ Backend connectivity test failed:', healthError);
        throw new Error(`Cannot connect to backend server. Please ensure the backend is running on ${baseUrl}. Error: ${healthError.message}`);
      }

      // Now make the actual certificate issuance request
      // Increased timeout to 600 seconds (10 minutes) to allow for auto-confirmation with up to 15 retries
      // Each confirmation retry can take up to 30s, so 15 retries = 450s max, plus buffer
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 600000); // 600 second timeout (10 minutes)
      
      let response;
      try {
        response = await fetch(apiUrl, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(applicationData),
          signal: controller.signal,
          mode: 'cors', // Explicitly enable CORS
          credentials: 'include' // Include credentials for CORS
        });
        clearTimeout(timeoutId);
      } catch (fetchError) {
        clearTimeout(timeoutId);
        if (fetchError.name === 'AbortError') {
          throw new Error('Request timeout - the server took too long to respond (over 2 minutes)');
        }
        console.error('❌ Fetch error:', fetchError);
        // Re-throw to be caught by outer catch
        throw fetchError;
      }

      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }

      const result = await response.json();
      
      // Check for hard errors (ER005, ER006) - these should block payment
      if (result.doNotRetry && (result.errorCode === 'ER005' || result.errorCode === 'ER006' || result.errorCode === 'ER003' || result.errorCode === 'ER004' || result.errorCode === 'ERR10001')) {
        setIsValidatingCertificate(false);
        setDmvicCertificate({
          ...dmvicCertificate,
          hardError: true,
          error: result.message || 'Certificate cannot be issued due to a critical error'
        });
        
        // Show hard error message
        let errorMsg = '❌ Certificate Cannot Be Issued\n\n';
        if (result.errorCode === 'ER005') {
          errorMsg += 'Double Insurance Detected: This vehicle already has an active insurance policy.\n\n';
          
          // Show conflicting policy details if available
          if (result.existingPolicies && result.existingPolicies.length > 0) {
            const activePolicies = result.existingPolicies.filter(p => p.status === 'Active');
            const expiredPolicies = result.existingPolicies.filter(p => p.status === 'Expired');
            
            if (activePolicies.length > 0) {
              errorMsg += `📋 Found ${activePolicies.length} Active Conflicting Policy(ies):\n`;
              errorMsg += '━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n';
              activePolicies.forEach((policy, index) => {
                errorMsg += `\n${index + 1}. Certificate Number: ${policy.certificateNumber || 'N/A'}\n`;
                errorMsg += `   Insurance Company: ${policy.companyName || 'Unknown'}\n`;
                errorMsg += `   Expiry Date: ${policy.endDate || 'Unknown'}\n`;
                if (policy.daysUntilExpiry !== null && policy.daysUntilExpiry > 0) {
                  errorMsg += `   Days Remaining: ${policy.daysUntilExpiry}\n`;
                }
                if (policy.registrationNumber) {
                  errorMsg += `   Registration: ${policy.registrationNumber}\n`;
                }
                if (policy.chassisNumber) {
                  errorMsg += `   Chassis: ${maskChassisNumber(policy.chassisNumber)}\n`;
                }
              });
              errorMsg += '\n';
            } else if (expiredPolicies.length > 0) {
              errorMsg += `⚠️ Found ${expiredPolicies.length} Policy(ies) - All Appear Expired:\n`;
              errorMsg += '━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n';
              expiredPolicies.forEach((policy, index) => {
                errorMsg += `\n${index + 1}. Certificate Number: ${policy.certificateNumber || 'N/A'}\n`;
                errorMsg += `   Insurance Company: ${policy.companyName || 'Unknown'}\n`;
                errorMsg += `   Expired: ${policy.endDate || 'Unknown'}\n`;
              });
              errorMsg += '\n⚠️ If these policies are expired, this may be a false positive.\n';
              errorMsg += 'Please contact DMVIC support to verify and resolve.\n\n';
            }
          }
          
          errorMsg += 'You cannot proceed with payment. Please contact DMVIC support if you believe this is an error.';
        } else if (result.errorCode === 'ER006') {
          errorMsg += 'Inventory Error: There is an issue with the vehicle inventory.\n\n';
          errorMsg += 'You cannot proceed with payment. Please contact support.';
        } else if (result.errorCode === 'ERR10001') {
          // ERR10001: NTSA Data Mismatch - show detailed comparison
          errorMsg += 'NTSA Data Mismatch: Vehicle details do not match official NTSA records.\n\n';
          
          if (result.ntsaData || result.response?.callbackObj?.NTSAResponse) {
            const ntsaData = result.ntsaData || result.response?.callbackObj?.NTSAResponse;
            errorMsg += '📋 NTSA Official Records:\n';
            errorMsg += '━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━\n';
            if (ntsaData.ChassisNumber) errorMsg += `Chassis: ${ntsaData.ChassisNumber}\n`;
            if (ntsaData.EngineNumber) errorMsg += `Engine: ${ntsaData.EngineNumber}\n`;
            if (ntsaData.VehicleMake) errorMsg += `Make: ${ntsaData.VehicleMake}\n`;
            if (ntsaData.VehicleModel) errorMsg += `Model: ${ntsaData.VehicleModel}\n`;
            if (ntsaData.BodyType) errorMsg += `Body Type: ${ntsaData.BodyType}\n`;
            if (ntsaData.VehicleRegistrationYear) errorMsg += `Year: ${ntsaData.VehicleRegistrationYear}\n`;
            errorMsg += '\n';
          }
          
          errorMsg += '⚠️ The system attempted to auto-correct using NTSA data but certificate issuance still failed.\n\n';
          errorMsg += 'Please verify all vehicle details match the official NTSA records above and try again, or contact support.';
        } else {
          errorMsg += result.message || 'A critical error prevents certificate issuance.';
        }
        
        showPersistentNotification(errorMsg, 'error');
        return; // Block payment
      }
      
      // Check if certificate was issued successfully
      if (result.success || result.certificateNumber) {
        // Certificate validated/issued successfully - allow payment
        setIsValidatingCertificate(false);
        setCertificateValidated(true);
        setDmvicCertificate({
          ...dmvicCertificate,
          certificateNumber: result.certificateNumber,
          transactionNumber: result.transactionNumber,
          error: null,
          hardError: false
        });
        
        showNotification('✅ Certificate validated successfully! You can now proceed to payment.', 'success', 5000);
      } else if ((result.needsConfirmation || result.issuanceRequestID) && result.issuanceRequestID) {
        // ER007/ER002 warnings - AUTO-CONFIRM intelligently
        // Don't show scary warnings to users - handle silently in background
        const issuanceRequestID = result.issuanceRequestID || result.response?.callbackObj?.IssuanceRequestID;
        
        setIsValidatingCertificate(false);
        
        // Auto-confirm immediately - don't show warnings
        setDmvicCertificate(prevState => ({
          ...prevState,
          isIssuing: true, // Show loading state
          certificateNumber: null,
          transactionNumber: null,
          error: null, // Don't show error to user
          retryCount: 0,
          queuedForRetry: false,
          issuanceRequestID: issuanceRequestID,
          pendingConfirmation: false // Auto-confirming, not pending
        }));
        
        // Show simple loading message
        showNotification('⏳ Processing certificate issuance...', 'info', 3000);
        
        // Auto-confirm immediately
        setTimeout(async () => {
          await handleConfirmAndProceed(0, issuanceRequestID);
        }, 500);
        
        // Allow payment to proceed - auto-confirmation is happening in background
        return;
      } else {
        // Validation failed for other reasons
        setIsValidatingCertificate(false);
        setDmvicCertificate({
          ...dmvicCertificate,
          error: result.message || 'Certificate validation failed',
          hardError: false
        });
        showNotification(`Certificate validation failed: ${result.message || 'Unknown error'}`, 'error');
      }
    } catch (error) {
      setIsValidatingCertificate(false);
      
      // Enhanced error handling for network errors
      let errorMessage = error.message;
      if (error.message.includes('NetworkError') || error.message.includes('Failed to fetch') || error.name === 'TypeError') {
        const apiUrl = API_ENDPOINTS?.issueCertificate || 'http://10.25.30.30:5000/api/dmvic/issue-certificate';
        errorMessage = `Network error: Unable to connect to the server at ${apiUrl}. Please check your internet connection and ensure the backend is running.`;
        console.error('Network error details:', {
          message: error.message,
          name: error.name,
          stack: error.stack,
          apiUrl: apiUrl,
          apiEndpoints: API_ENDPOINTS,
          windowLocation: typeof window !== 'undefined' ? window.location.href : 'N/A'
        });
      }
      
      setDmvicCertificate({
        ...dmvicCertificate,
        error: errorMessage,
        hardError: false
      });
      showNotification(`Certificate validation failed: ${errorMessage}`, 'error');
    }
  };

  const refreshAgentProfileAndContext = async () => {
    const agentToken =
      typeof localStorage !== 'undefined' &&
      (localStorage.getItem('agentToken') || localStorage.getItem('employeeToken'));
    if (!agentToken) throw new Error('Your session has expired. Please log in again.');

    const response = await fetch(API_ENDPOINTS.agentProfile, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${agentToken}`
      }
    });

    let result;
    try {
      result = await response.json();
    } catch (e) {
      result = null;
    }

    if (!response.ok || !result || !result.success) {
      const msg = (result && (result.error || result.message)) || `Failed to refresh profile (${response.status})`;
      throw new Error(msg);
    }

    const p = result.data || {};
    const nextAgency = {
      shortDescription: (p.tqCode != null ? String(p.tqCode) : '').trim(),
      taxPin: (p.kraPin != null ? String(p.kraPin) : '').trim()
    };
    const hasTq = nextAgency.shortDescription !== '';
    const hasPin = nextAgency.taxPin !== '';

    const base = resolvedAgentTransactionContext || {};
    const nextContext = {
      ...base,
      source: base.source || 'AGENTBIMA',
      transactionStartedAt: base.transactionStartedAt || new Date().toISOString(),
      agentId: base.agentId != null ? base.agentId : (agentId != null ? agentId : p.id),
      ...(base.agentUserId != null ? { agentUserId: base.agentUserId } : (agentUserId != null ? { agentUserId } : {})),
      agency: nextAgency
    };

    setAgentTransactionContextOverride(nextContext);
    persistAgentTransactionContext(nextContext);
    return { hasTq, hasPin, agency: nextAgency };
  };

  const handleProceedToPay = async () => {
    // Payment succeeded and certificate issued: optional "View receipt" (no auto-redirect)
    if (paymentStep === 'success' && (dmvicCertificate.certificateNumber || dmvicCertificate.certificateUrl) && clientId) {
      goToReceipt();
      return;
    }
    // Payment succeeded but certificate failed: allow user to go to receipt so the transaction does not "disappear"
    if (paymentStep === 'certificate-failed' && clientId) {
      goToReceipt();
      return;
    }
    // If the user already initiated payment and polling timed out, allow a safe re-check
    // without sending a new STK push (prevents duplicate charges).
    if (paymentStep === 'pending') {
      const storedRef = localStorage.getItem('agentbima_payment_reference') || localStorage.getItem('bimabot_payment_reference');
      const storedOtid = localStorage.getItem('agentbima_order_tracking_id') || localStorage.getItem('bimabot_order_tracking_id');
      const identifier = storedRef || storedOtid;
      if (!identifier) {
        showNotification('No pending payment reference found. Please retry payment.', 'warning');
        setPaymentStep('failed');
        return;
      }

      setIsProcessing(true);
      setPaymentStep('processing');
      showNotification('Checking payment status...', 'info');
      try {
        await waitForPaymentConfirmation(identifier);
      } catch (e) {
        setPaymentStep('pending');
        showNotification(`Unable to confirm payment yet: ${e.message}`, 'warning');
        setIsProcessing(false);
      }
      return;
    }

    // If payment failed due to missing agent TQ/KRA details, refresh profile on retry.
    if (paymentStep === 'failed' && lastPaymentError && lastPaymentError.code === 'PREFLIGHT_AGENT_MISSING_TQ_OR_PIN') {
      try {
        showNotification('Refreshing your profile details...', 'info', 2500);
        const refreshed = await refreshAgentProfileAndContext();
        if (!refreshed.hasTq || !refreshed.hasPin) {
          showNotification('Your profile is still missing TQ code or KRA PIN. Please update them in Profile, then retry payment.', 'error', 7000);
          return;
        }
        setLastPaymentError(null);
      } catch (e) {
        // Backend pre-flight is authoritative; if refresh fails, still allow a retry attempt.
        showNotification(`Could not refresh profile details: ${e.message}. Retrying payment anyway...`, 'warning', 6000);
      }
    }

    // Underwriting: Ecobank Mpesa code flow (validate code with Ecobank, REG_NO must match cover)
    if (isUnderwriting && paymentMethod === 'ecobank') {
      await handleEcobankValidate();
      return;
    }

    if (extensionSeedBlocked) {
      showNotification(EXTENSION_SEED_USER_MESSAGE, 'error', 12000);
      return;
    }

    // STRICT VALIDATION: Must have a phone number
    if (mobileNumber.trim() === '') {
      showNotification('Please enter a mobile number', 'error');
      return;
    }

    // STRICT VALIDATION: Must be a valid Kenyan number format
    if (!isValidMobileNumber()) {
      showNotification('Invalid phone number format. Please enter as: 0746542699, 0143167137, +254746542699, or 746542699', 'error');
      return;
    }

    // STRICT VALIDATION: Phone must have exactly 9 digits (after removing country code)
    const cleanNumber = mobileNumber.replace(/[^0-9]/g, '');
    let expectedDigits;
    
    if (mobileNumber.startsWith('+254') || mobileNumber.startsWith('254')) {
      expectedDigits = cleanNumber.length - 3; // Remove 254
    } else if (mobileNumber.startsWith('0')) {
      expectedDigits = cleanNumber.length - 1; // Remove 0
    } else {
      expectedDigits = cleanNumber.length;
    }
    
    if (expectedDigits !== 9) {
      showNotification(`Invalid phone number: Need exactly 9 digits after country code. Got ${expectedDigits}`, 'error');
      return;
    }

    // Start payment process
    setIsProcessing(true);
    setPaymentStep('processing');
    
    // Track payment initiation
    trackPaymentInitiated({
      coverType: coverType,
      vehicleClass: selectedClass,
      premiumAmount: payableAmount,
    });
    
    showNotification('Initiating MPESA payment...', 'info');
    
    try {
      // Flow: 1) Push – STK sent by backend. 2) Save – backend saves to payment_statuses (pending). 3) Auto-poll until completed.
      const stkPushResult = await initiateRealSTKPush();
      
      if (stkPushResult.success) {
        setLastPaymentError(null);
        showFlashNotification('STK Push sent! Enter your M-Pesa PIN on your phone – we’ll confirm automatically.', 'mpesa', 5000);
        const ref = stkPushResult.referenceNumber || stkPushResult.orderTrackingId;
        await waitForPaymentConfirmation(ref);
      } else {
        const failureCode = stkPushResult.code || null;
        setLastPaymentError({ code: failureCode, message: stkPushResult.error || 'STK Push failed' });

        if (failureCode === 'PREFLIGHT_AGENT_MISSING_TQ_OR_PIN') {
          setPaymentStep('failed');
          showNotification(`Payment failed: ${stkPushResult.error || 'Complete your profile (TQ code and KRA PIN) before initiating a purchase.'}`, 'error', 8000);
          setIsProcessing(false);
          return;
        }
        // Handle phone formatting errors intelligently
        if (stkPushResult.details) {
          const errorMsg = stkPushResult.error || 'Invalid phone number format';
          const suggestion = stkPushResult.details.suggestion || 'Try: 0746542699';
          showNotification(`❌ ${errorMsg}. ${suggestion}`, 'error');
        } else {
          throw new Error(stkPushResult.error || 'STK Push failed');
        }
      }
      
    } catch (error) {
      setPaymentStep('failed');
      showNotification(`Payment failed: ${error.message}`, 'error');
      setIsProcessing(false);
    }
  };

  /** COMP UAT: mark reserved cover paid locally, then run the normal issue-certificate path. */
  const handleSimulateCompUatPayment = async () => {
    if (!clientId) {
      showNotification('Please wait for cover reservation (UAT policy number) before simulating payment.', 'error');
      return;
    }
    if (!policyNumber) {
      showNotification('UAT policy number is not ready yet. Wait for reservation to finish.', 'error');
      return;
    }
    const issuedByAdminId = resolvedAgentTransactionContext?.issuedByAdminId;
    if (issuedByAdminId == null || issuedByAdminId === '') {
      showNotification('Comprehensive UAT simulation requires the pilot underwriting session.', 'error');
      return;
    }

    setIsProcessing(true);
    setPaymentStep('processing');
    showNotification('Simulating successful payment (COMP UAT)…', 'info');

    try {
      const payload = mergeAgentContextIntoRequestBody(
        {
          clientId,
          issuedByAdminId,
          amount: payableAmount,
          customerPhone: mobileNumber || insuredData?.phoneNumber || '',
          customerName: insuredData?.insuredName || insuredData?.insuredNameFull || 'COMP UAT',
          vehicleRegistration: vehicleData?.registrationNumber,
          applicationData: {
            clientId,
            policyNumber,
            coverType: 'COMP',
            vehicleClass: selectedClass,
            startDate,
            endDate: submissionEndDate,
            coverPeriod: submissionCoverPeriod,
            premiumAmount: payableAmount,
            insuredData: buildInsuredDataForSave(),
            vehicleDetails: vehicleDataForSave
          }
        },
        resolvedAgentTransactionContext
      );

      const response = await fetch(API_ENDPOINTS.simulateCompUatPayment, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      const result = await response.json().catch(() => ({}));
      if (!response.ok || !result.success) {
        throw new Error(result.error || `Simulate payment failed (HTTP ${response.status})`);
      }

      const referenceNumber = result.referenceNumber;
      if (referenceNumber) localStorage.setItem('agentbima_payment_reference', referenceNumber);
      if (result.policyNumber) setPolicyNumber(result.policyNumber);
      setPaymentStep('success');
      setIsProcessing(false);
      setDmvicCertificate((prev) => ({
        ...prev,
        isIssuing: true,
        error: null,
        errorCode: null,
        errorText: null
      }));
      showFlashNotification('Payment simulated. Issuing 1-month DMVIC UAT certificate…', 'success', 5000);
      logEvent('payment_success', {
        referenceNumber,
        simulated: true,
        clientId,
        policyNumber: result.policyNumber || policyNumber,
        coverType: 'COMP'
      });

      try {
        const saveResult = await saveApplicationDataAsync(
          {
            coverType: 'COMP',
            selectedClass,
            vehicleData,
            insuredData: buildInsuredDataForSave(),
            startDate,
            endDate: submissionEndDate,
            coverPeriod: submissionCoverPeriod,
            premium: { ...(premium || {}), amount: payableAmount },
            agentTransactionContext: resolvedAgentTransactionContext
          },
          referenceNumber
        );
        const issueClientId = saveResult?.clientId || clientId;
        if (saveResult?.policyNumber) setPolicyNumber(saveResult.policyNumber);
        if (issueClientId) {
          setClientId(issueClientId);
          issueDMVICCertificate(false, 0, issueClientId).catch((err) => {
            console.error('COMP UAT certificate issuance failed:', err);
            showNotification(
              'Payment simulated. Certificate issuance failed — retry from receipt or contact support.',
              'warning'
            );
          });
        }
      } catch (saveErr) {
        console.error('COMP UAT save after simulate failed:', saveErr);
        // Still attempt issue with reserved clientId
        issueDMVICCertificate(false, 0, clientId).catch(() => {});
      }
    } catch (error) {
      setPaymentStep('failed');
      showNotification(`Simulate payment failed: ${error.message}`, 'error');
      setIsProcessing(false);
    }
  };

  // Ecobank Mpesa code validation (underwriting only). Calls backend to verify code and REG_NO match; then same success flow as Pesapal.
  const handleEcobankValidate = async () => {
    if (extensionSeedBlocked) {
      showNotification(EXTENSION_SEED_USER_MESSAGE, 'error', 12000);
      return;
    }
    const extendPayBlock = getTpoExtendPaymentBlockMessage();
    if (extendPayBlock) {
      showNotification(extendPayBlock, 'error', 14000);
      return;
    }
    if (!clientId) {
      showNotification('Please complete the cover reservation first before validating payment.', 'error');
      return;
    }
    const ecoPlateCheck = validateKenyanVehicleRegistration(vehicleData?.registrationNumber, selectedClass, {
      chassisNumber: vehicleData?.chassisNumber,
      make: vehicleData?.make,
      model: vehicleData?.model,
      bodyType: vehicleData?.bodyType,
      allowChassisAsRegistration: true
    });
    if (!ecoPlateCheck.valid) {
      showNotification(ecoPlateCheck.message, 'error', 8000);
      return;
    }
    const code = (ecobankMpesaCode || '').trim();
    if (!code) {
      showNotification('Enter the M-Pesa code from the customer\'s payment (e.g. UCHLR9B36K).', 'error');
      return;
    }
    setIsProcessing(true);
    setPaymentStep('processing');
    showNotification('Verifying M-Pesa code with Ecobank...', 'info');
    try {
      const effectiveAgentId = (resolvedAgentTransactionContext && resolvedAgentTransactionContext.agentId != null && resolvedAgentTransactionContext.agentId !== '')
        ? resolvedAgentTransactionContext.agentId
        : agentId;
      const effectiveAgentUserId = (resolvedAgentTransactionContext && resolvedAgentTransactionContext.agentUserId != null && resolvedAgentTransactionContext.agentUserId !== '')
        ? resolvedAgentTransactionContext.agentUserId
        : agentUserId;
      const effectiveAgency = (resolvedAgentTransactionContext && resolvedAgentTransactionContext.agency)
        ? resolvedAgentTransactionContext.agency
        : agency;
      const applicationDataBase = {
        coverType,
        selectedClass,
        institutionType: resolvedInstitutionType,
        vehicleData: vehicleDataForSave,
        insuredData: buildInsuredDataForSave(),
        startDate,
        endDate: submissionEndDate,
        coverPeriod: submissionCoverPeriod,
        premium: premium,
        ...(coverType === 'TPO_EXTEND'
          ? {
              productVariant: 'TPO_EXTEND',
              ...(extensionContext?.policyNo
                ? {
                    extensionMode: true,
                    extensionPolicyNo: extensionContext.policyNo,
                    extensionEffectiveDate: startDate,
                    extensionToDate: submissionEndDate,
                  }
                : { extensionMode: false })
            }
          : {}),
        ...(renewalContext?.sourceClientId
          ? {
              isRenewal: true,
              renewedFromClientId: renewalContext.sourceClientId,
              renewedFromPolicyNumber: renewalContext.sourcePolicyNumber || null
            }
          : {})
      };
      const applicationDataWithAgent = mergeAgentContextIntoRequestBody(applicationDataBase, resolvedAgentTransactionContext);
      if (!applicationDataWithAgent.isDirectBusiness) {
        if (effectiveAgency && typeof effectiveAgency === 'object' && (effectiveAgency.shortDescription || effectiveAgency.taxPin)) {
          applicationDataWithAgent.agency = effectiveAgency;
        }
        if (effectiveAgentId != null && effectiveAgentId !== '') applicationDataWithAgent.agentId = effectiveAgentId;
        if (typeof effectiveAgentUserId !== 'undefined' && effectiveAgentUserId != null && effectiveAgentUserId !== '') {
          applicationDataWithAgent.agentUserId = effectiveAgentUserId;
        }
      }
      const adminToken = typeof localStorage !== 'undefined' && localStorage.getItem('adminToken');
      if (!adminToken) {
        setPaymentStep('input');
        setIsProcessing(false);
        showNotification('Session expired. Please log in again as underwriting.', 'error');
        return;
      }
      const response = await fetch(API_ENDPOINTS.verifyEcobankMpesaCode, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${adminToken}`,
        },
        body: JSON.stringify({
          clientId,
          mpesaCode: code,
          applicationData: applicationDataWithAgent,
          amount: payableAmount,
          customerName: insuredData?.insuredNameFull || insuredData?.insuredName || 'Customer',
          vehicleRegistration: vehicleData?.registrationNumber || 'N/A',
          ...(applicationDataWithAgent.isDirectBusiness
            ? {}
            : { agentId: effectiveAgentId, agentUserId: effectiveAgentUserId }),
        }),
      });
      const result = await response.json();
      if (!result.success) {
        setPaymentStep('input');
        setIsProcessing(false);
        const errMsg = result.error || 'Verification failed.';
        const code = result.code || '';
        const friendlyPrefix = code === 'ECOBANK_REG_NO_MISMATCH'
          ? 'Registration mismatch: '
          : code === 'ECOBANK_AMOUNT_MISMATCH'
            ? 'Amount mismatch: '
            : code === 'ECOBANK_CODE_ALREADY_USED'
              ? 'Code already used: '
              : '';
        showNotification(friendlyPrefix ? `${friendlyPrefix}${errMsg}` : errMsg, 'error', 10000);
        return;
      }
      setLastPaymentError(null);
      if (result.referenceNumber) localStorage.setItem('agentbima_payment_reference', result.referenceNumber);
      if (result.orderTrackingId) localStorage.setItem('agentbima_order_tracking_id', result.orderTrackingId);
      if (result.clientId) setClientId(result.clientId);
      if (result.policyNumber) setPolicyNumber(result.policyNumber);
      setPaymentStep('success');
      setIsProcessing(false);
      setDmvicCertificate(prev => ({ ...prev, isIssuing: true, error: null, errorCode: null, errorText: null }));
      showFlashNotification('Payment verified. Your policy is being processed...', 'success', 5000);
      try {
        const saveResult = await saveApplicationDataAsync({
          coverType,
          selectedClass,
          vehicleData,
          insuredData: buildInsuredDataForSave(),
          startDate,
          endDate: submissionEndDate,
          coverPeriod: submissionCoverPeriod,
          premium: { ...(premium || {}), amount: payableAmount },
          agentTransactionContext: resolvedAgentTransactionContext,
        }, result.referenceNumber);
        if (saveResult?.clientId) {
          setClientId(saveResult.clientId);
          if (saveResult.policyNumber) setPolicyNumber(saveResult.policyNumber);
          issueDMVICCertificate(false, 0, saveResult.clientId).catch((err) => {
            console.error('Certificate issuance after Ecobank verify failed:', err);
            showNotification('Payment verified. Certificate is being retried in the background.', 'warning');
          });
        }
      } catch (saveErr) {
        console.error('Save application after Ecobank verify failed:', saveErr);
        showNotification('Payment verified. Saving policy data failed – please contact support with your payment reference.', 'warning');
      }
    } catch (error) {
      setPaymentStep('input');
      setIsProcessing(false);
      showNotification(error.message || 'Verification failed.', 'error');
    }
  };

  // Initiate Pesapal STK Push; server requires clientId (reserve-first: create client before payment).
  const initiateRealSTKPush = async () => {
    if (!clientId) {
      showNotification('Please complete the cover reservation first (summary step) before paying.', 'error');
      return { success: false, error: 'Reserve a cover first. Client ID is required before initiating payment.', code: 'STK_CLIENT_ID_REQUIRED' };
    }
    const plateCheck = validateKenyanVehicleRegistration(vehicleData?.registrationNumber, selectedClass, {
      chassisNumber: vehicleData?.chassisNumber,
      make: vehicleData?.make,
      model: vehicleData?.model,
      bodyType: vehicleData?.bodyType,
      allowChassisAsRegistration: true
    });
    if (!plateCheck.valid) {
      showNotification(plateCheck.message, 'error', 8000);
      return { success: false, error: plateCheck.message, code: 'INCOMPLETE_VEHICLE_REGISTRATION' };
    }
    const extendPayBlock = getTpoExtendPaymentBlockMessage();
    if (extendPayBlock) {
      showNotification(extendPayBlock, 'error', 14000);
      return { success: false, error: extendPayBlock, code: 'EXTENSION_ANNUAL_END_REQUIRED' };
    }
    try {
      const effectiveAgentId = (resolvedAgentTransactionContext && resolvedAgentTransactionContext.agentId != null && resolvedAgentTransactionContext.agentId !== '')
        ? resolvedAgentTransactionContext.agentId
        : agentId;
      const effectiveAgentUserId = (resolvedAgentTransactionContext && resolvedAgentTransactionContext.agentUserId != null && resolvedAgentTransactionContext.agentUserId !== '')
        ? resolvedAgentTransactionContext.agentUserId
        : agentUserId;
      const effectiveAgency = (resolvedAgentTransactionContext && resolvedAgentTransactionContext.agency)
        ? resolvedAgentTransactionContext.agency
        : agency;

      const applicationDataBase = {
        coverType,
        selectedClass,
        institutionType: resolvedInstitutionType,
        vehicleData: vehicleDataForSave,
        insuredData: buildInsuredDataForSave(),
        startDate,
        endDate: submissionEndDate,
        coverPeriod: submissionCoverPeriod,
        premium: premium,
        ...(coverType === 'TPO_EXTEND'
          ? {
              productVariant: 'TPO_EXTEND',
              ...(extensionContext?.policyNo
                ? {
                    extensionMode: true,
                    extensionPolicyNo: extensionContext.policyNo,
                    extensionEffectiveDate: startDate,
                    extensionToDate: submissionEndDate,
                  }
                : { extensionMode: false })
            }
          : {}),
        ...(renewalContext?.sourceClientId
          ? {
              isRenewal: true,
              renewedFromClientId: renewalContext.sourceClientId,
              renewedFromPolicyNumber: renewalContext.sourcePolicyNumber || null
            }
          : {})
      };
      const applicationDataWithAgent = mergeAgentContextIntoRequestBody(applicationDataBase, resolvedAgentTransactionContext);
      if (!applicationDataWithAgent.isDirectBusiness) {
        if (effectiveAgency && typeof effectiveAgency === 'object' && (effectiveAgency.shortDescription || effectiveAgency.taxPin)) {
          applicationDataWithAgent.agency = effectiveAgency;
        }
        if (effectiveAgentId != null && effectiveAgentId !== '') applicationDataWithAgent.agentId = effectiveAgentId;
        if (typeof effectiveAgentUserId !== 'undefined' && effectiveAgentUserId != null && effectiveAgentUserId !== '') {
          applicationDataWithAgent.agentUserId = effectiveAgentUserId;
        }
      }
      const paymentData = mergeAgentContextIntoRequestBody(
        {
          transactionId: `AGENTBIMA-${Date.now()}`,
          source: 'AGENTBIMA',
          amount: payableAmount,
          customerName: insuredData?.insuredNameFull || insuredData?.insuredName || 'Customer',
          customerPhone: mobileNumber,
          vehicleRegistration: vehicleData?.registrationNumber || 'N/A',
          customerEmail: insuredData?.email || `${mobileNumber.replace(/[^\d]/g, '').slice(-9)}@monarchinsurance.co.ke`,
          ...(clientId ? { clientId } : {}),
          applicationData: applicationDataWithAgent,
        },
        resolvedAgentTransactionContext
      );
      if (!applicationDataWithAgent.isDirectBusiness) {
        if (effectiveAgentId != null && effectiveAgentId !== '') paymentData.agentId = effectiveAgentId;
        if (typeof effectiveAgentUserId !== 'undefined' && effectiveAgentUserId != null && effectiveAgentUserId !== '') {
          paymentData.agentUserId = effectiveAgentUserId;
        }
      }

      const headers = { 'Content-Type': 'application/json' };
      const agentToken = typeof localStorage !== 'undefined' && (localStorage.getItem('agentToken') || localStorage.getItem('employeeToken'));
      if (agentToken) headers['Authorization'] = `Bearer ${agentToken}`;

      const response = await fetch(API_ENDPOINTS.initiateSTKPush, {
        method: 'POST',
        headers,
        body: JSON.stringify(paymentData)
      });

      const result = await response.json();
      
      // Store payment reference and order tracking ID for certificate issuance
      if (result.success) {
        if (result.referenceNumber) {
          localStorage.setItem('agentbima_payment_reference', result.referenceNumber);
        }
        if (result.orderTrackingId) {
          localStorage.setItem('agentbima_order_tracking_id', result.orderTrackingId);
        }
      }
      
      return result;
    } catch (error) {
      console.error('❌ Pesapal STK Push error:', error);
      return {
        success: false,
        error: error.message
      };
    }
  };

  // Auto-poll payment status until completed/failed/timeout (same flow as scripts/test-payment-1kes.js --wait).
  const waitForPaymentConfirmation = (referenceNumber) => {
    const isSimulation = referenceNumber.startsWith('SIM-');
    const intervalMs = isSimulation ? 10000 : 2000;  // 2s for real (match test script)
    const maxAttempts = isSimulation ? 6 : 60;       // 60 x 2s = 120s timeout
    let attempts = 0;
    let pollInterval = null;

    const checkPaymentStatus = async () => {
      try {
        const response = await fetch(API_ENDPOINTS.paymentStatus(referenceNumber));
        if (!response.ok) return { complete: false, status: null };
        const result = await response.json();
        if (result.success && result.status === 'completed') {
          setPaymentStep('success');
          setIsProcessing(false);
          // Set isIssuing immediately so receipt redirect does not run before certificate is ready
          setDmvicCertificate(prev => ({ ...prev, isIssuing: true, error: null, errorCode: null, errorText: null }));
          showFlashNotification('Payment successful! Your policy is being processed...', 'success', 5000);
          if (result.referenceNumber) localStorage.setItem('agentbima_payment_reference', result.referenceNumber);
          if (result.orderTrackingId) localStorage.setItem('agentbima_order_tracking_id', result.orderTrackingId);
          if (result.clientId) setClientId(result.clientId);
          if (result.policyNumber) setPolicyNumber(result.policyNumber);
          logEvent('payment_success', { referenceNumber, orderTrackingId: result.orderTrackingId, clientId: result.clientId, policyNumber: result.policyNumber });
          // Observe the already-queued IPN job immediately. Do not wait for save/issue —
          // those can lag while DMVIC has already issued and Core has already been sent.
          if (result.certificateNumber || result.downloadReady) {
            applyCertificateReady({ ...result, downloadReady: true }, result.jobId || null);
          } else {
            const pollClientId = result.clientId;
            const jobId = result.jobId || paidCertJobIdFromRef(result.referenceNumber || referenceNumber);
            if (jobId) startCertificateJobPolling(jobId, pollClientId);
            else if (pollClientId) startCertificateStatusPolling(pollClientId);
          }
          try {
            const saveResult = await saveApplicationDataAsync({
              coverType,
              selectedClass,
              vehicleData,
              insuredData: buildInsuredDataForSave(),
              startDate,
              endDate: submissionEndDate,
              coverPeriod: submissionCoverPeriod,
              premium: { ...(premium || {}), amount: payableAmount },
              agentTransactionContext: resolvedAgentTransactionContext,
              // Keep second-half extend identity (same as STK / Ecobank) — do not rely only on React closure.
              extensionPolicyNo: extensionContext?.policyNo,
              productVariant:
                coverType === 'TPO_EXTEND'
                  ? 'TPO_EXTEND'
                  : undefined,
              extensionMode: !!(coverType === 'TPO_EXTEND' && extensionContext?.policyNo),
              extensionToDate:
                coverType === 'TPO_EXTEND' && extensionContext?.policyNo
                  ? submissionEndDate
                  : undefined,
            }, referenceNumber);
            if (saveResult?.clientId) {
              setClientId(saveResult.clientId);
              if (saveResult.policyNumber) setPolicyNumber(saveResult.policyNumber);
              logEvent('certificate_issuing', { referenceNumber, clientId: saveResult.clientId });
              issueDMVICCertificate(false, 0, saveResult.clientId).catch((err) => {
                console.error('Certificate issuance after payment failed:', err);
                logEvent('certificate_issuance_error', { referenceNumber, clientId: saveResult.clientId, error: err?.message || String(err) });
                showNotification('Payment successful. Certificate is being retried in the background.', 'warning');
              });
            }
          } catch (saveErr) {
            console.error('Save application after payment failed:', saveErr);
            logEvent('save_after_payment_failed', { referenceNumber, error: saveErr?.message || String(saveErr) });
            showNotification('Payment successful. Saving policy data failed – please contact support with your payment reference.', 'warning');
          }
          return { complete: true, status: 'completed' };
        }
        if (result.success && result.status === 'failed') {
          setPaymentStep('failed');
          showNotification('Payment was cancelled or not completed. Click "Retry Payment" to try again.', 'error');
          setIsProcessing(false);
          return { complete: true, status: 'failed' };
        }
        return { complete: false, status: result.status || 'pending' };
      } catch (error) {
        console.error('Error checking payment status:', error);
        return { complete: false, status: null };
      }
    };

    return new Promise((resolve) => {
      const onDone = () => {
        if (pollInterval) clearInterval(pollInterval);
        resolve();
      };

      const tick = async () => {
        attempts++;
        const result = await checkPaymentStatus();
        if (result.complete) {
          onDone();
          return;
        }
        if (attempts >= maxAttempts) {
          const finalCheck = await checkPaymentStatus();
          if (finalCheck.complete && finalCheck.status === 'completed') {
            onDone();
            return;
          }
          if (finalCheck.status === 'pending' || finalCheck.status == null) {
            setPaymentStep('pending');
            showNotification('Payment may still be processing. If you entered your PIN, click "Check Payment Status" to confirm.', 'warning');
          } else {
            setPaymentStep('failed');
            showNotification('Payment was not completed. Click "Retry Payment" to try again.', 'error');
          }
          setIsProcessing(false);
          onDone();
        }
      };

      tick(); // First check immediately
      pollInterval = setInterval(tick, intervalMs);
    });
  };

  const isValidMobileNumber = () => {
    return validateKenyanInsuredPhone(mobileNumber).valid;
  };

  // Get phone number status for visual feedback (similar to InsuredDetails)
  const getMobileNumberStatus = () => {
    if (!mobileNumber) return { status: 'empty', message: 'Enter mobile number', progress: 0 };
    
    const cleanNumber = mobileNumber.replace(/[^0-9]/g, '');
    const digitsAfterPrefix = mobileNumber.startsWith('+254') 
      ? cleanNumber.length - 3  // Remove 254
      : mobileNumber.startsWith('0')
        ? cleanNumber.length - 1  // Remove 0
        : cleanNumber.length;
    
    // Check if it starts with +254
    if (mobileNumber.startsWith('+254')) {
      const remaining = digitsAfterPrefix;
      if (remaining === 0) return { status: 'typing', message: 'Enter 9 digits after +254', progress: 0 };
      if (remaining < 9) return { status: 'typing', message: `${9 - remaining} digits remaining`, progress: (remaining / 9) * 100 };
      if (remaining === 9) return { status: 'valid', message: '✓ Valid MPESA number', progress: 100 };
      return { status: 'long', message: 'Too many digits (remove ' + (remaining - 9) + ')', progress: 100 };
    }
    
    // Check if it starts with 0 (local format)
    if (mobileNumber.startsWith('0')) {
      const remaining = digitsAfterPrefix;
      if (remaining < 9) return { status: 'typing', message: `${9 - remaining} digits remaining`, progress: (remaining / 9) * 100 };
      if (remaining === 9) return { status: 'valid', message: '✓ Valid MPESA number', progress: 100 };
      return { status: 'long', message: 'Too many digits (remove ' + (remaining - 9) + ')', progress: 100 };
    }
    
    // Check if it starts with 254
    if (mobileNumber.startsWith('254')) {
      const remaining = cleanNumber.length - 3;
      if (remaining < 9) return { status: 'typing', message: `${9 - remaining} digits remaining`, progress: (remaining / 9) * 100 };
      if (remaining === 9) return { status: 'valid', message: '✓ Valid MPESA number', progress: 100 };
      return { status: 'long', message: 'Too many digits', progress: 100 };
    }
    
    // Generic validation
    if (cleanNumber.length < 9) return { status: 'typing', message: `${9 - cleanNumber.length} digits remaining`, progress: (cleanNumber.length / 9) * 100 };
    if (cleanNumber.length > 12) return { status: 'long', message: 'Phone number too long', progress: 0 };
    if (cleanNumber.length >= 9 && cleanNumber.length <= 12) return { status: 'valid', message: '✓ Valid MPESA number', progress: 100 };
    
    return { status: 'typing', message: 'Enter phone number', progress: 0 };
  };

  const mobileStatus = getMobileNumberStatus();

  const getPaymentButtonText = () => {
    switch (paymentStep) {
      case 'processing':
        return isUnderwriting && paymentMethod === 'ecobank' ? '⏳ Verifying...' : '⏳ Waiting for Payment...';
      case 'success':
        return (dmvicCertificate.certificateNumber || dmvicCertificate.certificateUrl) ? 'View receipt' : '✅ Payment Successful!';
      case 'certificate-failed':
        return 'View receipt';
      case 'pending':
        return '🔍 Check Payment Status';
      case 'failed':
        return '🔄 Retry Payment';
      default:
        if (isUnderwriting && paymentMethod === 'ecobank') return 'Validate M-Pesa code';
        return `Proceed to Pay - KES ${payableAmount.toLocaleString()}`;
    }
  };

  const getPaymentButtonDisabled = () => {
    if (paymentStep === 'failed') {
      return !clientId || isProcessing || extensionSeedBlocked || !isValidMobileNumber();
    }
    if (paymentStep === 'pending') return isProcessing;
    if (paymentStep === 'certificate-failed') return false; // Allow "View receipt"
    if (paymentStep === 'success' && (dmvicCertificate.certificateNumber || dmvicCertificate.certificateUrl)) return false; // Allow "View receipt"
    if (isUnderwriting && paymentMethod === 'ecobank') {
      return isProcessing || paymentStep === 'success' || !clientId || !(ecobankMpesaCode || '').trim() || extensionSeedBlocked;
    }
    // Pesapal STK is linked to a reserved draft client; block pay until clientId exists.
    return isProcessing || !isValidMobileNumber() || paymentStep === 'success' || extensionSeedBlocked || !clientId;
  };

  const getPaymentButtonClass = () => {
    if (paymentStep === 'success') return 'btn-primary success';
    if (paymentStep === 'certificate-failed') return 'btn-primary success'; // Keep success style, allow going to receipt
    if (paymentStep === 'failed') return 'btn-primary error';
    return 'btn-primary checkout-btn';
  };

  const showNotification = (message, type = 'info', duration = 5000) => {
    setSnackbarMessage(message);
    setSnackbarType(type);
    setSnackbarDuration(duration);
    setShowSnackbar(true);
  };
  
  // Show persistent notification (no timeout) - for critical errors like NTSA mismatch
  const showPersistentNotification = (message, type = 'error') => {
    setSnackbarMessage(message);
    setSnackbarType(type);
    setSnackbarDuration(0); // 0 = persistent (no timeout)
    setShowSnackbar(true);
  };

  const showFlashNotification = (message, type = 'info', duration = 3000) => {
    setFlashMessage(message);
    setFlashType(type);
    setShowFlash(true);
  };

  // Manual retry function for failed saves
  const retryFailedSave = () => {
    if (certificateIssuedRef.current) {
      try { localStorage.removeItem('bimabot_pending_application'); } catch (_) { /* ignore */ }
      return;
    }
    const pendingData = localStorage.getItem('bimabot_pending_application');
    if (pendingData) {
      try {
        const applicationData = JSON.parse(pendingData);
        showNotification('Retrying to save your policy...', 'info');
        saveApplicationDataAsync(applicationData, null, 0);
      } catch (error) {
        showNotification('Unable to retry save. Please contact support.', 'error');
      }
    } else {
      showNotification('No pending data found to retry.', 'warning');
    }
  };

  // Async database save function with enhanced retry logic
  // Returns a promise that resolves with the saved clientId
  // paymentReference: when provided (e.g. after payment success), server links payment_statuses.client_id so certificate can be issued
  const saveApplicationDataAsync = async (applicationData, paymentReference = null, retryCount = 0) => {
    const maxRetries = 8; // Increased from 3 to 8 attempts
    if (certificateIssuedRef.current) {
      try { localStorage.removeItem('bimabot_pending_application'); } catch (_) { /* ignore */ }
      return { skipped: true, alreadyIssued: true };
    }

    try {
      // Validate required data
      if (!applicationData.insuredData || !applicationData.vehicleData) {
        throw new Error('Missing required application data');
      }
      
      const refForLink = paymentReference || localStorage.getItem('agentbima_payment_reference') || localStorage.getItem('bimabot_payment_reference');
      const isSecondHalfFromPayload =
        applicationData.coverType === 'TPO_EXTEND' &&
        !!(applicationData.extensionPolicyNo || extensionContext?.policyNo);
      const saveEndDate =
        isSecondHalfFromPayload
          ? (applicationData.extensionToDate || applicationData.endDate)
          : applicationData.endDate;
      const requestDataBase = {
        // Keep TPO_EXTEND identity for fresh + second-half so server does not coerce 1 Month → 1 Year
        coverType: applicationData.coverType || 'TPO',
        vehicleClass: applicationData.selectedClass,
        institutionType: applicationData.institutionType || resolvedInstitutionType,
        vehicleDetails: applicationData.vehicleData,
        insuredData: buildInsuredDataForSave(applicationData.insuredData),
        startDate: applicationData.startDate,
        coverPeriod: isSecondHalfFromPayload ? '1 Year (50%)' : applicationData.coverPeriod,
        endDate: saveEndDate,
        premiumAmount:
          (Number.isFinite(Number(payableAmount)) && Number(payableAmount) > 0
            ? Number(payableAmount)
            : null) ??
          applicationData.premium?.amount ??
          applicationData.premium ??
          0,
        pricingMode,
        commissionAllowed,
        selectedNetAmount: isPricingModeEnabledClass ? selectedNetAmount : null,
        selectedGrossAmount: isPricingModeEnabledClass ? selectedGrossAmount : null,
        pricingSource,
        ...(clientId ? { clientId } : {}),
        ...(refForLink ? { payment: { referenceNumber: refForLink } } : {}),
        ...(applicationData.coverType === 'TPO_EXTEND'
          ? {
              productVariant: 'TPO_EXTEND',
              ...(isSecondHalfFromPayload
                ? {
                    extensionMode: true,
                    extensionPolicyNo: applicationData.extensionPolicyNo || extensionContext?.policyNo,
                    extensionEffectiveDate: applicationData.startDate,
                    extensionToDate: saveEndDate,
                    commissionAllowed: 'No'
                  }
                : {
                    extensionMode: false
                  })
            }
          : {}),
      };
      const ctxForSave = agentTransactionContextOverride || applicationData.agentTransactionContext || agentTransactionContext;
      const requestData = mergeAgentContextIntoRequestBody(requestDataBase, ctxForSave);
      const effectiveAgentId = (ctxForSave && ctxForSave.agentId != null && ctxForSave.agentId !== '') ? ctxForSave.agentId : agentId;
      const effectiveAgentUserId = (ctxForSave && ctxForSave.agentUserId != null && ctxForSave.agentUserId !== '') ? ctxForSave.agentUserId : agentUserId;
      const effectiveAgency = (ctxForSave && ctxForSave.agency) ? ctxForSave.agency : agency;
      if (!requestData.isDirectBusiness) {
        if (!requestData.agentId && effectiveAgentId != null && effectiveAgentId !== '') requestData.agentId = effectiveAgentId;
        if (!requestData.agency && effectiveAgency && typeof effectiveAgency === 'object' && (effectiveAgency.shortDescription || effectiveAgency.taxPin)) {
          requestData.agency = effectiveAgency;
        }
        if (!requestData.agentUserId && effectiveAgentUserId != null && effectiveAgentUserId !== '') {
          requestData.agentUserId = effectiveAgentUserId;
        }
      }
      
      const response = await fetch(API_ENDPOINTS.submitApplication, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(requestData)
      });

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const result = await response.json();
      
      if (result.success) {
        // Store clientId and policyNumber from response (for receipt / onPaymentComplete)
        if (result.clientId) {
          setClientId(result.clientId);
        }
        if (result.policyNumber) {
          setPolicyNumber(result.policyNumber);
        }
        
        // Clear localStorage backup on successful save
        try {
          localStorage.removeItem('bimabot_pending_application');
        } catch (storageError) {
        }

        if (!certificateIssuedRef.current) {
          showNotification('Policy data saved successfully', 'success');
        }

        // Return clientId, policyNumber, referenceNumber (for save-before-payment STK push)
        return {
          clientId: result.clientId,
          policyNumber: result.policyNumber,
          referenceNumber: result.referenceNumber || null
        };
      } else {
        throw new Error(result.message || 'Database save failed');
      }
      
    } catch (error) {
      if (certificateIssuedRef.current) {
        try { localStorage.removeItem('bimabot_pending_application'); } catch (_) { /* ignore */ }
        if (saveRetryTimeoutRef.current) {
          clearTimeout(saveRetryTimeoutRef.current);
          saveRetryTimeoutRef.current = null;
        }
        return { skipped: true, alreadyIssued: true };
      }

      // Store data in localStorage as backup (include agent context so retries preserve business link)
      try {
        const backupData = {
          ...applicationData,
          agentTransactionContext: resolvedAgentTransactionContext || applicationData.agentTransactionContext || agentTransactionContext,
          retryCount,
          lastAttempt: new Date().toISOString(),
          error: error.message
        };
        localStorage.setItem('bimabot_pending_application', JSON.stringify(backupData));
      } catch (storageError) {
      }
      
      // Only retry if it's not a validation error and we haven't exceeded max retries
      if (!error.message.includes('Missing required application data') && retryCount < maxRetries) {
        // Enhanced retry intervals: 30s, 1min, 2min, 4min, 8min, 15min, 30min, 1hour
        const retryIntervals = [30000, 60000, 120000, 240000, 480000, 900000, 1800000, 3600000];
        const retryDelay = retryIntervals[retryCount] || 3600000; // Default to 1 hour for additional retries

        if (saveRetryTimeoutRef.current) {
          clearTimeout(saveRetryTimeoutRef.current);
        }
        saveRetryTimeoutRef.current = setTimeout(() => {
          saveRetryTimeoutRef.current = null;
          if (certificateIssuedRef.current) {
            try { localStorage.removeItem('bimabot_pending_application'); } catch (_) { /* ignore */ }
            return;
          }
          saveApplicationDataAsync(applicationData, paymentReference, retryCount + 1);
        }, retryDelay);

        if (certificateIssuedRef.current) return;

        // Progressive messaging based on retry count
        let message;
        if (retryCount < 3) {
          message = `Saving your policy... (attempt ${retryCount + 1}/${maxRetries})`;
        } else if (retryCount < 6) {
          message = `Still saving your policy... (attempt ${retryCount + 1}/${maxRetries})`;
        } else {
          message = `Almost done saving your policy... (attempt ${retryCount + 1}/${maxRetries})`;
        }
        
        showNotification(message, 'info');
      } else {
        if (certificateIssuedRef.current) return;
        if (retryCount >= maxRetries) {
          showNotification('Payment successful! We\'re having trouble saving your policy. Please contact support with your payment reference.', 'warning');
        } else {
          showNotification('Policy data validation failed', 'error');
        }
      }
    }
  };

  return (
    <div className="checkout">
      <div className="checkout-header">
        <h1 className="checkout-title">Payment Checkout</h1>
        {coverType !== 'TPO_EXTEND' && (
          <p className="checkout-subtitle">Complete your {coverType} insurance application with MPESA payment</p>
        )}
      </div>

      <div className="checkout-content">
        {extensionSeedBlocked && (
          <div
            role="alert"
            style={{
              marginBottom: 20,
              padding: '14px 16px',
              borderRadius: 8,
              background: '#fff4e5',
              border: '1px solid #e8a317',
              color: '#5c3d00',
              fontSize: 14,
              lineHeight: 1.5
            }}
          >
            {EXTENSION_SEED_USER_MESSAGE}
          </div>
        )}
        {/* Cover Summary Section */}
        <div className="summary-section">
          <div className="section-header">
            <h2>Cover Summary</h2>
            <div className="section-divider"></div>
          </div>
          
          <div className="summary-grid">
            {policyNumber && (
              <div className="summary-row" style={{ background: '#e8f5e9', padding: '8px 12px', borderRadius: '6px', marginBottom: '8px' }}>
                <span className="summary-label">Policy Number</span>
                <span className="summary-value" style={{ fontWeight: 600 }}>{policyNumber}</span>
              </div>
            )}
            {(String(coverType || '').toUpperCase() === 'COMP') && (
              <>
                <div className="summary-row">
                  <span className="summary-label">Cover Type</span>
                  <span className="summary-value">Comprehensive (1 Month)</span>
                </div>
                <div className="summary-row">
                  <span className="summary-label">Sum Insured</span>
                  <span className="summary-value">
                    KES {Number(vehicleData?.sumInsured || vehicleData?.vehicleValue || 0).toLocaleString()}
                  </span>
                </div>
                {premium?.annualTotal != null && (
                  <div className="summary-row">
                    <span className="summary-label">Annual Total</span>
                    <span className="summary-value">KES {Number(premium.annualTotal).toLocaleString()}</span>
                  </div>
                )}
                <div className="summary-row">
                  <span className="summary-label">Payable (30%)</span>
                  <span className="summary-value" style={{ fontWeight: 700 }}>
                    KES {Number(payableAmount || 0).toLocaleString()}
                  </span>
                </div>
              </>
            )}
            <div className="summary-row">
              <span className="summary-label">Insured Person</span>
              <span className="summary-value">{insuredData?.insuredName}</span>
            </div>
            {insuredPhoneNeedsMpesa && (
              <div
                className="summary-row"
                style={{
                  background: '#fff8e6',
                  border: '1px solid #e8a317',
                  borderRadius: 6,
                  padding: '8px 12px',
                  marginBottom: 8,
                  color: '#5c3d00',
                  fontSize: 14,
                  lineHeight: 1.45
                }}
              >
                Insured phone on file is invalid or matches vehicle value. Enter the correct M-Pesa number below — it will be saved for certificate issuance.
              </div>
            )}
            <div className="summary-row">
              <span className="summary-label">Vehicle</span>
              <span className="summary-value">{vehicleData?.make} ({vehicleData?.registrationNumber})</span>
            </div>
            <div className="summary-row">
              <span className="summary-label">Vehicle Class</span>
              <span className="summary-value">{selectedClass}</span>
            </div>
            <div className="summary-row">
              <span className="summary-label">Chassis</span>
              <span className="summary-value">
                {vehicleData?.chassisNumber 
                  ? maskChassisNumber(String(vehicleData.chassisNumber)) 
                  : 'N/A'}
              </span>
            </div>
            <div className="summary-row">
              <span className="summary-label">
                {coverType === 'TPO_EXTEND' && isTpoExtendSecondHalf ? 'Extension start' : 'Start Date'}
              </span>
              <span className="summary-value">{startDate}</span>
            </div>
            <div className="summary-row">
              <span className="summary-label">
                {coverType === 'TPO_EXTEND' && isTpoExtendSecondHalf ? 'Extension end' : 'End Date'}
              </span>
              <span className="summary-value">{coverType === 'TPO_EXTEND' ? submissionEndDate : endDate}</span>
            </div>
            {hasExtensionOutstandingBalance && (
              <div className="summary-row" style={{ color: '#8a5b00' }}>
                <span className="summary-label">Outstanding (Core)</span>
                <span className="summary-value">KES {Math.round(extensionPolicyBalance).toLocaleString()}</span>
              </div>
            )}
            <div className="summary-row">
              <span className="summary-label">
                {coverType === 'TPO_EXTEND' && isTpoExtendSecondHalf ? 'Term' : 'Cover Period'}
              </span>
              <span className="summary-value">
                {coverType === 'TPO_EXTEND' ? submissionCoverPeriodDisplay : coverPeriod}
              </span>
            </div>
            <div className="summary-row">
              <span className="summary-label">Cover Type</span>
              <span className="summary-value">{coverType === 'TPO_EXTEND' ? 'TPO' : coverType}</span>
            </div>
          </div>

          <div className="premium-highlight">
            <div className="premium-content">
              <span className="premium-label">Total Premium</span>
              <span className="premium-amount">KES {payableAmount.toLocaleString()}</span>
            </div>
          </div>
          {isPricingModeEnabledClass && (
            <div style={{ marginTop: 10, padding: '10px 12px', borderRadius: 8, background: '#f8f9fa', border: '1px solid #dee2e6' }}>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>Pricing mode</div>
              <div style={{ display: 'flex', gap: '1.25rem', flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: isProcessing ? 'not-allowed' : 'pointer' }}>
                  <input
                    type="radio"
                    name="pricingMode"
                    value="NET"
                    checked={pricingMode === 'NET'}
                    onChange={() => setPricingMode('NET')}
                    disabled={isProcessing}
                  />
                  <span>Net (KES {selectedNetAmount.toLocaleString()})</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: isProcessing ? 'not-allowed' : 'pointer' }}>
                  <input
                    type="radio"
                    name="pricingMode"
                    value="GROSS"
                    checked={pricingMode === 'GROSS'}
                    onChange={() => setPricingMode('GROSS')}
                    disabled={isProcessing}
                  />
                  <span>Gross (KES {selectedGrossAmount.toLocaleString()})</span>
                </label>
              </div>
              <div style={{ marginTop: 8, fontSize: '0.9rem', color: '#495057' }}>
                {pricingMode === 'GROSS'
                  ? <>You are paying the <span style={modePillStyle}>GROSS</span> price. This attracts Commission.</>
                  : <>You are paying the <span style={modePillStyle}>NET</span> price. No Commission is attracted.</>}
              </div>
            </div>
          )}
        </div>

        {/* Payment Section - Payment First Flow */}
        <div className="payment-section">
          <div className="section-header">
            <h2>MPESA Payment</h2>
            <div className="section-divider"></div>
          </div>
          
          <div id="paymentAmount" className="payment-amount-display">
            <div className="amount-content">
              <span className="amount-label">Amount to Pay</span>
              <span className="amount-value">KES {payableAmount.toLocaleString()}</span>
            </div>
            {isPricingModeEnabledClass && (
              <p className="payment-description" style={{ marginBottom: 8 }}>
                Payment mode: <span style={modePillStyle}>{pricingMode}</span>
              </p>
            )}
            <p className="payment-description">
              {isUnderwriting && paymentMethod === 'ecobank'
                ? 'Enter the M-Pesa code from the customer\'s Ecobank payment to validate and complete this cover.'
                : 'You will receive an MPESA STK Push notification on your mobile phone to complete the payment.'}
            </p>
          </div>

          {(!isUnderwriting || paymentMethod === 'pesapal') && !clientId && reserveState.status === 'failed' && (
            <div
              style={{
                marginTop: '0.75rem',
                marginBottom: '1rem',
                padding: '10px 12px',
                borderRadius: '8px',
                border: '1px solid #f5c2c7',
                background: '#f8d7da',
                color: '#842029'
              }}
            >
              <div style={{ fontWeight: 600, marginBottom: '6px' }}>Could not reserve this cover yet.</div>
              <div style={{ marginBottom: '8px' }}>
                {reserveState.error || 'Please retry before payment.'}
              </div>
              <button
                type="button"
                className="btn-secondary"
                onClick={handleRetryReserveCover}
                disabled={isProcessing}
              >
                Retry
              </button>
            </div>
          )}

          {isCompCover && (
            <div
              className="comp-prod-banner"
              style={{
                marginBottom: '1rem',
                padding: '12px 14px',
                borderRadius: 8,
                background: '#e8f5e9',
                border: '1px solid #a5d6a7',
                color: '#1b5e20',
                fontSize: 14,
                lineHeight: 1.45
              }}
            >
              <strong>Comprehensive</strong> — 1 month cover · pay 30% of annual · live policy number ·
              certificate only after successful payment · Core posting after cert.
              {policyNumber ? (
                <div style={{ marginTop: 6 }}>
                  Reserved policy: <strong>{policyNumber}</strong>
                </div>
              ) : (
                <div style={{ marginTop: 6 }}>Waiting for policy reservation…</div>
              )}
            </div>
          )}

          {isUnderwriting && (
            <div className="payment-method-choice" style={{ marginBottom: '1rem' }}>
              <span className="input-label" style={{ display: 'block', marginBottom: '8px' }}>Payment method</span>
              <div style={{ display: 'flex', gap: '1.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="pesapal"
                    checked={paymentMethod === 'pesapal'}
                    onChange={() => setPaymentMethod('pesapal')}
                    disabled={isProcessing}
                  />
                  <span>Pesapal (STK Push)</span>
                </label>
                <label style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="paymentMethod"
                    value="ecobank"
                    checked={paymentMethod === 'ecobank'}
                    onChange={() => setPaymentMethod('ecobank')}
                    disabled={isProcessing}
                  />
                  <span>Ecobank (M-Pesa code)</span>
                </label>
              </div>
            </div>
          )}

          {(!isUnderwriting || paymentMethod === 'pesapal') && (
          <div className="mobile-input-section">
            <label htmlFor="mobileNumber" className="input-label">Mobile Number for MPESA</label>
            <div className="input-with-status">
              <input
                type="tel"
                id="mobileNumber"
                value={mobileNumber}
                onChange={handleMobileNumberChange}
                onBlur={handleMobileNumberBlur}
                onFocus={() => handleFocus('mobileNumber')}
                placeholder="e.g., +254 700 000 000 or 0700 000 000"
                required
                className={`mobile-input ${mobileStatus.status}`}
                disabled={isProcessing}
              />
              {mobileNumber && (
                <div className="phone-status-indicator" style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px',
                  marginTop: '8px',
                  padding: '8px',
                  borderRadius: '4px',
                  backgroundColor: mobileStatus.status === 'valid' ? '#d4edda' : 
                                   mobileStatus.status === 'long' ? '#f8d7da' : '#fff3cd',
                  border: mobileStatus.status === 'valid' ? '1px solid #c3e6cb' : 
                          mobileStatus.status === 'long' ? '1px solid #f5c6cb' : '1px solid #ffeeba'
                }}>
                  <span className={`status-icon ${mobileStatus.status}`} style={{
                    fontSize: '1.2em',
                    color: mobileStatus.status === 'valid' ? '#28a745' : 
                           mobileStatus.status === 'long' ? '#dc3545' : '#ffc107'
                  }}>
                    {mobileStatus.status === 'valid' ? '✓' : 
                     mobileStatus.status === 'empty' ? '⭕' : 
                     mobileStatus.status === 'long' ? '⚠' : '⌛'}
                  </span>
                  <span className={`status-message ${mobileStatus.status}`} style={{
                    fontSize: '0.9em',
                    color: mobileStatus.status === 'valid' ? '#155724' : 
                           mobileStatus.status === 'long' ? '#721c24' : '#856404',
                    fontWeight: 500
                  }}>
                    {mobileStatus.message}
                  </span>
                </div>
              )}
            </div>
            <small className="input-help">
              Enter the mobile number registered with MPESA. This number will receive the payment prompt.
              {mobileStatus.status === 'valid' && ' ✓ Ready to pay!'}
            </small>
          </div>
          )}

          {isUnderwriting && paymentMethod === 'ecobank' && (
          <div className="ecobank-mpesa-code-section" style={{ marginBottom: '1rem' }}>
            <label htmlFor="ecobankMpesaCode" className="input-label">M-Pesa code</label>
            <div className="input-with-status">
              <input
                type="text"
                id="ecobankMpesaCode"
                value={ecobankMpesaCode}
                onChange={(e) => setEcobankMpesaCode((e.target.value || '').trim().toUpperCase())}
                placeholder="e.g. UCHLR9B36K"
                className="mobile-input"
                disabled={isProcessing}
                style={{ textTransform: 'uppercase' }}
              />
            </div>
            <small className="input-help">
              Enter the M-Pesa code from the customer&apos;s payment. The vehicle registration (REG_NO) from Ecobank must match this cover&apos;s registration.
            </small>
          </div>
          )}

          {(!isUnderwriting || paymentMethod === 'pesapal') && (
          <div className="mpesa-info">
            <div className="info-icon">📱</div>
            <div className="info-content">
              <h4>MPESA Payment Process</h4>
              <ul>
                <li>Enter your MPESA registered mobile number</li>
                <li>Click "Proceed to Pay" to initiate payment</li>
                <li>You&apos;ll receive an STK Push notification</li>
                <li>Enter your MPESA PIN to complete payment</li>
                <li>Payment confirmation will be sent via SMS</li>
              </ul>
            </div>
          </div>
          )}

          {/* Payment Status */}
          {paymentStep === 'processing' && (
            <div className="payment-status processing">
              <div className="status-icon">⏳</div>
              <div className="status-content">
                <h4>Waiting for You to Complete Payment</h4>
                <p>STK Push sent! Check your phone and enter your MPESA PIN.</p>
                <div className="loading-dots">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            </div>
          )}

          {paymentStep === 'success' && (
            <div className="payment-status success">
              <div className="status-icon">✅</div>
              <div className="status-content">
                <h4>Payment Successful!</h4>
                <p>Your insurance policy is being processed. You will receive confirmation shortly.</p>
                
                {/* DMVIC Certificate Status - wait here until certificate is ready or failed */}
                {(dmvicCertificate.isIssuing || dmvicCertificate.certificateJobId) && !(dmvicCertificate.certificateNumber || dmvicCertificate.certificateUrl) && (
                  <div className="dmvic-status" style={{ 
                    marginTop: '15px', 
                    padding: '12px 15px', 
                    background: '#e7f3ff', 
                    borderRadius: '6px',
                    border: '1px solid #b6d4fe'
                  }}>
                    <p style={{ margin: '0', color: '#084298', fontWeight: 500 }}>
                      {issuingStatusCopy.title}
                    </p>
                    <p style={{ margin: '6px 0 0 0', color: '#055160', fontSize: '0.9em' }}>
                      {issuingStatusCopy.body}
                    </p>
                  </div>
                )}
                
                {(dmvicCertificate.certificateNumber || dmvicCertificate.certificateUrl) && (
                  <div className="dmvic-certificate-info" style={{ 
                    marginTop: '15px', 
                    padding: '15px', 
                    background: '#d4edda', 
                    borderRadius: '4px',
                    border: '1px solid #c3e6cb'
                  }}>
                    <h5 style={{ color: '#155724', margin: '0 0 10px 0' }}>✅ DMVIC Certificate Issued</h5>
                    {dmvicCertificate.certificateNumber && (
                    <p style={{ margin: '5px 0', color: '#155724' }}>
                      <strong>Certificate Number:</strong> {dmvicCertificate.certificateNumber}
                    </p>
                    )}
                    {dmvicCertificate.transactionNumber && (
                      <p style={{ margin: '5px 0', color: '#155724' }}>
                        <strong>Transaction Number:</strong> {dmvicCertificate.transactionNumber}
                      </p>
                    )}
                    <p style={{ margin: '8px 0 0 0', color: '#155724', fontSize: '0.9em' }}>
                      The certificate has also been sent to the email address you entered. Please check your inbox.
                    </p>
                    {dmvicCertificate.recordingOnCore && (
                      <p style={{ margin: '6px 0 0 0', color: '#155724', fontSize: '0.85em' }}>
                        We're finishing recording this on our system. You can download now.
                      </p>
                    )}
                    <div style={{ marginTop: '10px', display: 'flex', flexWrap: 'wrap', gap: '12px', alignItems: 'center' }}>
                      <button
                        onClick={handleDownloadCertificate}
                        style={{
                          background: '#28a745',
                          color: 'white',
                          border: 'none',
                          padding: '8px 16px',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '0.9em',
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: '5px',
                          fontWeight: 600
                        }}
                      >
                        📄 Download Certificate
                      </button>
                      <span style={{ color: '#155724', fontSize: '0.8em' }}>
                        <em>{dmvicCertificate.certificateUrl ? 'Click to download cover PDF' : 'Certificate PDF is ready for download'}</em>
                      </span>
                      {clientId && policyNumber && (
                        <a
                          href="#"
                          onClick={(e) => { e.preventDefault(); goToReceipt(); }}
                          style={{
                            fontSize: '0.9em',
                            color: '#0d6efd',
                            textDecoration: 'underline',
                            marginLeft: '8px'
                          }}
                        >
                          View receipt
                        </a>
                      )}
                    </div>
                  </div>
                )}
                
                {dmvicCertificate.queuedForRetry && (
                  <div className="dmvic-retry" style={{ 
                    marginTop: '15px', 
                    padding: '15px', 
                    background: '#fff3cd', 
                    borderRadius: '4px',
                    border: '1px solid #ffeeba'
                  }}>
                    <h5 style={{ color: '#856404', margin: '0 0 10px 0' }}>🔄 Certificate Issuance in Progress</h5>
                    <p style={{ margin: '5px 0', color: '#856404' }}>
                      Certificate issuance failed initially, but has been queued for automatic retry.
                    </p>
                    <p style={{ margin: '5px 0', color: '#856404' }}>
                      <strong>Retry attempt:</strong> {dmvicCertificate.retryCount || 0} / {dmvicCertificate.maxRetries || 5}
                    </p>
                    <p style={{ margin: '5px 0', color: '#856404', fontSize: '0.9em' }}>
                      <em>We're automatically retrying in the background. Please wait...</em>
                    </p>
                  </div>
                )}
                
                {/* ER007 warnings - needs manual confirmation (broker-APIS approach) */}
                {/* Debug: Log button visibility state */}
                {/* Auto-confirmation is handled in background - don't show warning button to users */}
                {/* Do not show "Confirm & Proceed" while the retry/error panel is visible (same session can otherwise show both). */}
                {dmvicCertificate.issuanceRequestID && !dmvicCertificate.certificateNumber && !dmvicCertificate.isIssuing && !(dmvicCertificate.error && dmvicCertificate.canRetry) && (
                  <div className="dmvic-confirmation" style={{ 
                    marginTop: '15px', 
                    padding: '20px', 
                    background: '#fff3cd', 
                    borderRadius: '8px',
                    border: '2px solid #ffc107',
                    boxShadow: '0 2px 8px rgba(255, 193, 7, 0.2)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
                      <span style={{ fontSize: '1.5em' }}>⚠️</span>
                      <h5 style={{ color: '#856404', margin: '0', fontSize: '1.1em' }}>Certificate Requires Confirmation</h5>
                    </div>
                    <p style={{ margin: '10px 0', color: '#856404', lineHeight: '1.6' }}>
                      {dmvicCertificate.error || 'DMVIC requires a confirmation step before the certificate can be issued. Click below to confirm and proceed.'}
                    </p>
                    {dmvicCertificate.issuanceRequestID && (
                      <p style={{ margin: '10px 0', color: '#856404', fontSize: '0.9em', fontFamily: 'monospace', background: '#fff', padding: '8px', borderRadius: '4px' }}>
                        <strong>Request ID:</strong> {dmvicCertificate.issuanceRequestID}
                      </p>
                    )}
                    <button
                      onClick={() => handleConfirmAndProceed()}
                      disabled={dmvicCertificate.isIssuing}
                      style={{
                        marginTop: '15px',
                        background: '#ffc107',
                        color: '#000',
                        border: 'none',
                        padding: '12px 24px',
                        borderRadius: '6px',
                        cursor: dmvicCertificate.isIssuing ? 'not-allowed' : 'pointer',
                        fontSize: '1em',
                        fontWeight: 'bold',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        gap: '8px',
                        opacity: dmvicCertificate.isIssuing ? 0.6 : 1,
                        width: '100%',
                        boxShadow: dmvicCertificate.isIssuing ? 'none' : '0 2px 4px rgba(0,0,0,0.2)',
                        transition: 'all 0.2s ease'
                      }}
                      onMouseOver={(e) => {
                        if (!dmvicCertificate.isIssuing) {
                          e.target.style.background = '#ffb300';
                          e.target.style.transform = 'translateY(-1px)';
                        }
                      }}
                      onMouseOut={(e) => {
                        if (!dmvicCertificate.isIssuing) {
                          e.target.style.background = '#ffc107';
                          e.target.style.transform = 'translateY(0)';
                        }
                      }}
                    >
                      {dmvicCertificate.isIssuing ? (
                        <>
                          <span className="spinner" style={{
                            display: 'inline-block',
                            width: '14px',
                            height: '14px',
                            border: '2px solid #000',
                            borderTopColor: 'transparent',
                            borderRadius: '50%',
                            animation: 'spin 0.8s linear infinite'
                          }}></span>
                          Confirming...
                        </>
                      ) : (
                        <>
                          <span style={{ fontSize: '1.2em' }}>✓</span>
                          Confirm & Proceed
                        </>
                      )}
                    </button>
                    <p style={{ margin: '10px 0 0 0', color: '#856404', fontSize: '0.85em', fontStyle: 'italic' }}>
                      Click the button above to confirm the warnings and proceed with certificate issuance.
                    </p>
                  </div>
                )}

                {/* Show retry options if max retries reached */}
                {dmvicCertificate.error && dmvicCertificate.canRetry && !dmvicCertificate.certificateNumber && (
                  <div className="dmvic-retry" style={{ 
                    marginTop: '15px', 
                    padding: '20px', 
                    background: '#f8d7da', 
                    borderRadius: '8px',
                    border: '2px solid #dc3545',
                    boxShadow: '0 2px 8px rgba(220, 53, 69, 0.2)'
                  }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '15px' }}>
                      <span style={{ fontSize: '1.5em' }}>⚠️</span>
                      <h5 style={{ color: '#721c24', margin: '0', fontSize: '1.1em' }}>
                        {dmvicCertificate.failedJobId
                          ? 'Certificate Issuance Failed'
                          : dmvicCertificate.certificateJobId
                            ? 'Certificate still in progress (or service unavailable)'
                            : 'Certificate Confirmation Failed'}
                      </h5>
                    </div>
                    <p style={{ margin: '10px 0', color: '#721c24', lineHeight: '1.6' }}>
                      {dmvicCertificate.error}
                    </p>
                    {(dmvicCertificate.errorCode || dmvicCertificate.errorText) && (
                      <div style={{ margin: '12px 0', padding: '12px', background: '#fff', borderRadius: '6px', border: '1px solid #dc3545', fontSize: '0.9em' }}>
                        <strong style={{ color: '#721c24' }}>Exact error (for rectification):</strong>
                        {dmvicCertificate.errorCode && (
                          <p style={{ margin: '6px 0 2px 0', color: '#721c24', fontFamily: 'monospace' }}><strong>Code:</strong> {dmvicCertificate.errorCode}</p>
                        )}
                        {dmvicCertificate.errorText && (
                          <p style={{ margin: '2px 0 0 0', color: '#721c24', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}><strong>Details:</strong> {dmvicCertificate.errorText}</p>
                        )}
                      </div>
                    )}
                    {typeof localStorage !== 'undefined' && localStorage.getItem('agentbima_payment_reference') && (
                      <p style={{ margin: '10px 0', color: '#721c24', fontSize: '0.9em', fontFamily: 'monospace', background: '#fff', padding: '8px', borderRadius: '4px' }}>
                        <strong>Payment reference (for support):</strong> {localStorage.getItem('agentbima_payment_reference')}
                      </p>
                    )}
                    {dmvicCertificate.issuanceRequestID && !dmvicCertificate.failedJobId && (
                      <p style={{ margin: '10px 0', color: '#721c24', fontSize: '0.9em', fontFamily: 'monospace', background: '#fff', padding: '8px', borderRadius: '4px' }}>
                        <strong>Request ID:</strong> {dmvicCertificate.issuanceRequestID}
                      </p>
                    )}
                    {dmvicCertificate.retryCount > 0 && (
                      <p style={{ margin: '10px 0', color: '#721c24', fontSize: '0.9em' }}>
                        <strong>Retry attempts:</strong> {dmvicCertificate.retryCount} / {dmvicCertificate.maxRetries || 10}
                      </p>
                    )}
                    <div style={{ marginTop: '15px' }}>
                      {dmvicCertificate.failedJobId || dmvicCertificate.certificateJobId ? (
                        <p style={{ margin: '0 0 10px 0', color: '#721c24', fontSize: '0.9em', fontStyle: 'italic' }}>
                          💡 Your payment was successful. {dmvicCertificate.certificateJobId && !dmvicCertificate.failedJobId
                            ? 'The certificate may still be issuing — use the button to check status or keep waiting.'
                            : 'You can retry certificate issuance below.'}
                        </p>
                      ) : (
                        <p style={{ margin: '0 0 10px 0', color: '#721c24', fontSize: '0.9em', fontStyle: 'italic' }}>
                          💡 Your payment was successful. We're automatically retrying in the background. You can also retry manually below.
                        </p>
                      )}
                      <button
                        onClick={() => (dmvicCertificate.failedJobId || dmvicCertificate.certificateJobId) ? handleManualRetry() : handleConfirmAndProceed(0, dmvicCertificate.issuanceRequestID)}
                        disabled={dmvicCertificate.isIssuing}
                        style={{
                          background: dmvicCertificate.failedJobId || dmvicCertificate.certificateJobId ? '#dc3545' : '#28a745',
                          color: '#fff',
                          border: 'none',
                          padding: '12px 24px',
                          borderRadius: '6px',
                          cursor: dmvicCertificate.isIssuing ? 'not-allowed' : 'pointer',
                          fontSize: '1em',
                          fontWeight: 'bold',
                          width: '100%'
                        }}
                      >
                        {dmvicCertificate.failedJobId
                          ? '🔄 Retry certificate issuance'
                          : dmvicCertificate.certificateJobId
                            ? '🔁 Check status / keep waiting'
                            : '🔄 Retry confirmation now'}
                      </button>
                    </div>
                  </div>
                )}

                {dmvicCertificate.error && !dmvicCertificate.queuedForRetry && !dmvicCertificate.pendingConfirmation && !dmvicCertificate.canRetry && (
                  <div className="dmvic-error" style={{ 
                    marginTop: '15px', 
                    padding: '15px', 
                    background: '#f8d7da', 
                    borderRadius: '4px',
                    border: '1px solid #f5c6cb'
                  }}>
                    <h5 style={{ color: '#721c24', margin: '0 0 10px 0' }}>⚠️ Certificate Issuance Failed</h5>
                    <p style={{ margin: '5px 0', color: '#721c24' }}>
                      {dmvicCertificate.error}
                    </p>
                    {(dmvicCertificate.errorCode || dmvicCertificate.errorText) && (
                      <div style={{ margin: '10px 0', padding: '10px', background: '#fff', borderRadius: '4px', border: '1px solid #f5c6cb', fontSize: '0.9em' }}>
                        <strong style={{ color: '#721c24' }}>Exact error (for rectification):</strong>
                        {dmvicCertificate.errorCode && (
                          <p style={{ margin: '4px 0 2px 0', color: '#721c24', fontFamily: 'monospace' }}><strong>Code:</strong> {dmvicCertificate.errorCode}</p>
                        )}
                        {dmvicCertificate.errorText && (
                          <p style={{ margin: '2px 0 0 0', color: '#721c24', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}><strong>Details:</strong> {dmvicCertificate.errorText}</p>
                        )}
                      </div>
                    )}
                    {typeof localStorage !== 'undefined' && localStorage.getItem('agentbima_payment_reference') && (
                      <p style={{ margin: '8px 0', color: '#721c24', fontSize: '0.9em', fontFamily: 'monospace', background: '#fff', padding: '6px 8px', borderRadius: '4px' }}>
                        <strong>Payment reference (for support):</strong> {localStorage.getItem('agentbima_payment_reference')}
                      </p>
                    )}
                    {dmvicCertificate.retryCount > 0 && (
                      <p style={{ margin: '5px 0', color: '#721c24', fontSize: '0.9em' }}>
                        Retry attempts: {dmvicCertificate.retryCount}
                      </p>
                    )}
                    <button
                      onClick={() => handleManualRetry()}
                      disabled={dmvicCertificate.isIssuing}
                      style={{
                        marginTop: '10px',
                        background: '#dc3545',
                        color: 'white',
                        border: 'none',
                        padding: '8px 16px',
                        borderRadius: '4px',
                        cursor: dmvicCertificate.isIssuing ? 'not-allowed' : 'pointer',
                        fontSize: '0.9em',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                        opacity: dmvicCertificate.isIssuing ? 0.6 : 1
                      }}
                    >
                      🔄 Retry Certificate Issuance
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}

          {paymentStep === 'certificate-failed' && (
            <div className="payment-status success" style={{ borderColor: '#dc3545' }}>
              <div className="status-icon">✅</div>
              <div className="status-content">
                {(() => {
                  const err = dmvicCertificate.error || '';
                  const isInsufficientInventory = /insufficient inventory|INSUFFICIENT_INVENTORY|ER006/i.test(err);
                  const canFixDoubleInsurance = isDoubleInsuranceFailure(dmvicCertificate.errorCode, dmvicCertificate.errorText, dmvicCertificate.error);
                  const canFixEmail = isInvalidEmailFailure(dmvicCertificate.errorCode, dmvicCertificate.errorText, dmvicCertificate.error);
                  const nextAvailableDate = rectificationState.suggestedStartDate;
                  const canReissueDoubleInsurance = canFixDoubleInsurance && !!nextAvailableDate && !rectificationState.loadingSuggestion;
                  const reissueButtonLabel = (() => {
                    if (dmvicCertificate.isIssuing) return 'Re-issuing certificate…';
                    if (canFixEmail && canFixDoubleInsurance) {
                      return `Update email and re-issue from ${formatPolicyDate(nextAvailableDate)}`;
                    }
                    if (canFixDoubleInsurance) {
                      return `Re-issue certificate from ${formatPolicyDate(nextAvailableDate)}`;
                    }
                    return 'Save changes and re-issue';
                  })();
                  return (
                    <>
                      <h4>{isInsufficientInventory ? 'Payment Successful – Certificate Inventory Temporarily Low' : 'Payment Successful – Certificate Issue'}</h4>
                      {!canFixDoubleInsurance && (
                        <div style={{ marginBottom: '16px' }}>
                          <button
                            type="button"
                            onClick={() => handleManualRetry()}
                            disabled={dmvicCertificate.isIssuing}
                            style={{
                              background: dmvicCertificate.isIssuing ? '#6c757d' : '#dc3545',
                              color: '#fff',
                              border: 'none',
                              padding: '12px 24px',
                              borderRadius: '6px',
                              cursor: dmvicCertificate.isIssuing ? 'not-allowed' : 'pointer',
                              fontSize: '1.05em',
                              fontWeight: 'bold',
                              width: '100%',
                              maxWidth: '320px',
                              opacity: dmvicCertificate.isIssuing ? 0.8 : 1,
                              boxShadow: '0 2px 6px rgba(220, 53, 69, 0.3)'
                            }}
                          >
                            {dmvicCertificate.isIssuing
                              ? '⏳ Working…'
                              : dmvicCertificate.failedJobId
                                ? '🔄 Retry certificate issuance'
                                : dmvicCertificate.certificateJobId
                                  ? '🔁 Check status / keep waiting'
                                  : '🔄 Retry certificate issuance'}
                          </button>
                        </div>
                      )}
                      <p>
                        {canFixDoubleInsurance
                          ? 'Your payment was successful, but DMVIC blocked certificate issuance because this vehicle already has active cover. We will re-issue using the next available start date below.'
                          : isInsufficientInventory
                            ? 'Certificate inventory is temporarily low. Your policy is saved and paid. You can retry now using the button above, or retry later from your dashboard.'
                            : dmvicCertificate.certificateJobId && !dmvicCertificate.failedJobId
                              ? 'Your payment was successful. A certificate job may still be running in the background. Use the button to check again, or use dashboard retry if needed.'
                              : 'Your payment was successful, but the insurance certificate could not be issued. Your policy is saved. Please use the reference below if you contact support.'}
                      </p>
                      {typeof localStorage !== 'undefined' && localStorage.getItem('agentbima_payment_reference') && (
                        <p style={{ margin: '12px 0', fontFamily: 'monospace', background: '#f8f9fa', padding: '10px', borderRadius: '4px', fontSize: '0.95em' }}>
                          <strong>Payment reference:</strong> {localStorage.getItem('agentbima_payment_reference')}
                        </p>
                      )}
                      {err && (
                        <p style={{ margin: '8px 0', color: '#721c24', fontSize: '0.9em' }}>{err}</p>
                      )}
                      {(dmvicCertificate.errorCode || dmvicCertificate.errorText) && (
                        <div style={{ margin: '12px 0', padding: '12px', background: '#fff', borderRadius: '6px', border: '1px solid #dc3545', fontSize: '0.9em' }}>
                          <strong style={{ color: '#721c24' }}>Exact error (for rectification):</strong>
                          {dmvicCertificate.errorCode && (
                            <p style={{ margin: '6px 0 2px 0', color: '#721c24', fontFamily: 'monospace' }}><strong>Code:</strong> {dmvicCertificate.errorCode}</p>
                          )}
                          {dmvicCertificate.errorText && (
                            <p style={{ margin: '2px 0 0 0', color: '#721c24', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}><strong>Details:</strong> {dmvicCertificate.errorText}</p>
                          )}
                        </div>
                      )}
                      {(canFixDoubleInsurance || canFixEmail) && (
                        <div style={{ margin: '12px 0', padding: '12px', background: '#fff', borderRadius: '6px', border: '1px solid #dc3545' }}>
                          <h5 style={{ margin: '0 0 10px 0', color: '#721c24' }}>
                            {canFixDoubleInsurance ? 'Re-issue after active cover ends' : 'Fix and Re-issue'}
                          </h5>
                          {canFixEmail && (
                            <div style={{ marginBottom: '10px' }}>
                              <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px', color: '#212529' }}>
                                Correct email address
                              </label>
                              <input
                                type="email"
                                value={rectificationState.emailAddress}
                                onChange={(e) => setRectificationState(prev => ({ ...prev, emailAddress: e.target.value }))}
                                placeholder="name@example.com"
                                style={{ width: '100%', padding: '10px', borderRadius: '4px', border: '1px solid #ced4da' }}
                              />
                            </div>
                          )}
                          {canFixDoubleInsurance && (
                            <div style={{ marginBottom: '10px' }}>
                              {rectificationState.loadingSuggestion ? (
                                <p style={{ margin: 0, color: '#004085', fontSize: '0.9em' }}>
                                  Checking DMVIC for the next available start date…
                                </p>
                              ) : nextAvailableDate ? (
                                <div style={{
                                  padding: '12px',
                                  background: '#e7f3ff',
                                  border: '1px solid #b3d9ff',
                                  borderRadius: '6px',
                                  color: '#004085',
                                  fontSize: '0.9em'
                                }}>
                                  {rectificationState.existingPolicyEndDate && (
                                    <p style={{ margin: '0 0 8px 0' }}>
                                      Existing active cover ends on <strong>{formatPolicyDate(rectificationState.existingPolicyEndDate)}</strong>.
                                    </p>
                                  )}
                                  <p style={{ margin: 0 }}>
                                    Next available start date: <strong>{formatPolicyDate(nextAvailableDate)}</strong>
                                    <span style={{ display: 'block', marginTop: '4px', fontSize: '0.85em' }}>
                                      (day after the previous policy expires — this date will be saved and sent to DMVIC)
                                    </span>
                                  </p>
                                </div>
                              ) : (
                                <div style={{
                                  padding: '12px',
                                  background: '#f8d7da',
                                  border: '1px solid #f5c6cb',
                                  borderRadius: '6px',
                                  color: '#721c24',
                                  fontSize: '0.9em'
                                }}>
                                  <p style={{ margin: 0, fontWeight: 600 }}>Cannot auto-reissue yet.</p>
                                  <p style={{ margin: '8px 0 0 0' }}>
                                    {rectificationState.suggestionError ||
                                      'No next available date could be calculated. Please contact D-MVIC support.'}
                                  </p>
                                </div>
                              )}
                            </div>
                          )}
                          {rectificationState.applyError && (
                            <p style={{ margin: '8px 0', color: '#721c24', fontSize: '0.9em' }}>{rectificationState.applyError}</p>
                          )}
                          {(canReissueDoubleInsurance || canFixEmail) && (
                            <button
                              type="button"
                              onClick={handleApplyRectificationAndRetry}
                              disabled={dmvicCertificate.isIssuing || (canFixDoubleInsurance && !canReissueDoubleInsurance)}
                              style={{
                                marginTop: '8px',
                                width: '100%',
                                background: '#198754',
                                color: '#fff',
                                border: 'none',
                                padding: '10px 14px',
                                borderRadius: '6px',
                                fontWeight: 600,
                                cursor: (dmvicCertificate.isIssuing || (canFixDoubleInsurance && !canReissueDoubleInsurance)) ? 'not-allowed' : 'pointer',
                                opacity: (dmvicCertificate.isIssuing || (canFixDoubleInsurance && !canReissueDoubleInsurance)) ? 0.7 : 1
                              }}
                            >
                              {reissueButtonLabel}
                            </button>
                          )}
                        </div>
                      )}
                      <p style={{ marginTop: '12px', fontSize: '0.9em' }}>
                        {canFixDoubleInsurance
                          ? 'Re-issuing updates the policy dates in our system and sends the corrected dates to DMVIC.'
                          : isInsufficientInventory
                            ? 'Retry now or go to your dashboard and retry from the business list when ready.'
                            : 'You can retry certificate issuance using the button above or contact support with your payment reference.'}
                      </p>
                    </>
                  );
                })()}
              </div>
            </div>
          )}

          {paymentStep === 'pending' && (
            <div className="payment-status processing">
              <div className="status-icon">🕒</div>
              <div className="status-content">
                <h4>Payment Processing</h4>
                <p>
                  If you entered your M-Pesa PIN, the payment may complete shortly.
                  Click &quot;Check Payment Status&quot; to confirm without sending a new STK push.
                </p>
              </div>
            </div>
          )}

          {paymentStep === 'failed' && (
            <div className="payment-status error">
              <div className="status-icon">❌</div>
              <div className="status-content">
                <h4>Payment Failed</h4>
                <p>There was an issue processing your payment. Please try again or contact support.</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Navigation Buttons */}
      <div className="checkout-actions">
        <button 
          className="btn-secondary" 
          onClick={onBack}
          disabled={isProcessing && paymentStep !== 'failed'}
        >
          ← {renewalContext?.sourceClientId ? 'Back to renewal' : 'Back to Vehicle Details'}
        </button>
        <button
          id="proceedToPayButton"
          className={getPaymentButtonClass()}
          onClick={handleProceedToPay}
          disabled={getPaymentButtonDisabled()}
        >
          {getPaymentButtonText()}
        </button>
      </div>

      {/* Snackbar Notifications */}
      {showSnackbar && (
        <Snackbar
          message={snackbarMessage}
          type={snackbarType}
          onClose={() => setShowSnackbar(false)}
          duration={snackbarDuration}
        />
      )}

      {/* Flash Messages */}
      {showFlash && (
        <FlashMessage
          message={flashMessage}
          type={flashType}
          duration={4000}
          onComplete={() => setShowFlash(false)}
          position="center"
        />
      )}
    </div>
  );
};

export default Checkout;
